import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Clock3,
  Minus,
  Plus,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import ChaafoLogo from "../Images/Item_Image_Placeholder.webp";

import RepeatCustomizationModal from "./RepeatCustomizationModal";

import {
  buildCustomizationHistoryKey,
  syncCustomizationHistoryWithCart,
} from "../Utils/customizationPreferenceHistory";
import { useCatalogue } from "../Services/useCatalogue";
import { useCart } from "../Context/CartContext";
import CartDrawer from "./CartDrawer";
import { useGlobalContext } from "../Context/GlobalContext";
import { useRestaurantList } from "../Services/useRestaurantList";

// =========================================================
// PRICE DETAILS
// =========================================================

const getPriceDetails = (item) => {
  const prices = Array.isArray(item?.prices) ? item.prices : [];

  if (prices.length === 0) {
    return {
      priceOptions: [],
      customizations: [],
    };
  }

  const priceOptions = prices.map((priceItem, index) => {
    const originalPrice = Number(priceItem?.price || 0);

    const discountPrice = Number(priceItem?.discount_price || 0);

    // IMPORTANT:
    // API me discount "" aa sakta hai,
    // but discount_price valid ho sakta hai.
    const hasDiscount = discountPrice > 0 && discountPrice < originalPrice;

    return {
      index,

      size:
        String(priceItem?.size || priceItem?.size_trans || "Regular").trim() ||
        "Regular",

      sizeId: priceItem?.size_id || "",

      originalPrice,

      sellingPrice: hasDiscount ? discountPrice : originalPrice,

      formattedSellingPrice: hasDiscount
        ? priceItem?.formatted_discount_price || `₹${discountPrice}`
        : priceItem?.formatted_price || `₹${originalPrice}`,

      formattedOriginalPrice: hasDiscount
        ? priceItem?.formatted_price || `₹${originalPrice}`
        : "",

      hasDiscount,

      customizations: Array.isArray(priceItem?.customizations)
        ? priceItem.customizations
        : [],

      raw: priceItem,
    };
  });

  return {
    priceOptions,

    customizations: priceOptions.flatMap((option) => option.customizations),
  };
};

const buildSelectedVariantItem = (item, priceOption) => {
  if (!priceOption) {
    return item;
  }

  const selectedSize =
    String(priceOption?.size || "Regular").trim() || "Regular";

  // Agar backend size_id blank bheje,
  // size name ko fallback ID bana denge.
  const selectedSizeId =
    String(priceOption?.sizeId || selectedSize).trim() || selectedSize;

  const selectedSellingPrice = Number(priceOption?.sellingPrice || 0);

  return {
    ...item,

    // IMPORTANT:
    // CartContext ko sirf selected price variant mile.
    prices: priceOption?.raw ? [priceOption.raw] : item?.prices,

    // Selected variant
    size: selectedSize,
    sizeId: selectedSizeId,
    size_id: selectedSizeId,

    selected_size: selectedSize,
    selected_size_id: selectedSizeId,

    // Exact selected price
    unitPrice: selectedSellingPrice,
    itemPrice: selectedSellingPrice,
    item_price: selectedSellingPrice,

    selectedPrice: priceOption?.raw || null,

    hasDiscount: Boolean(priceOption?.hasDiscount),

    formattedPrice: priceOption?.hasDiscount
      ? priceOption?.formattedOriginalPrice
      : priceOption?.formattedSellingPrice,

    finalFormattedPrice: priceOption?.formattedSellingPrice,
  };
};

// =========================================================
// NORMALIZE ORDER TYPE
// =========================================================

const normalizeOrderType = (orderType) => {
  return String(orderType || "")
    .trim()
    .toLowerCase()
    .replaceAll("-", "")
    .replaceAll("_", "")
    .replaceAll(" ", "");
};

// =========================================================
// ITEM AVAILABILITY
// =========================================================

const getItemAvailability = (item, orderType) => {
  if (item?.inStock !== true) {
    return false;
  }

  const normalizedOrderType = normalizeOrderType(orderType);

  if (
    normalizedOrderType === "homedelivery" ||
    normalizedOrderType === "delivery"
  ) {
    return item?.homeDeliveryAvailability !== false;
  }

  if (normalizedOrderType === "dinein" || normalizedOrderType === "dining") {
    return item?.diningAvailability !== false;
  }

  return item?.takeawayAvailability !== false;
};

const menuFilters = [
  {
    id: "all",
    label: "All",
  },
  {
    id: "150-300",
    label: "₹150 - ₹300",
  },
  {
    id: "above-300",
    label: "Above ₹300",
  },
  {
    id: "pure-veg",
    label: "Veg",
  },
  {
    id: "non-veg",
    label: "Non Veg",
  },
];

// =========================================================
// MENU ITEM FILTER HELPERS
// =========================================================

const getMenuItemRating = (item) => {
  const rating = Number(
    item?.ratings ??
      item?.rating ??
      0,
  );

  return Number.isFinite(rating)
    ? rating
    : 0;
};

const getMenuItemStartingPrice = (item) => {
  const prices = Array.isArray(item?.prices)
    ? item.prices
    : [];

  const validPrices = prices
    .map((priceItem) => {
      const originalPrice = Number(
        priceItem?.price || 0,
      );

      const discountPrice = Number(
        priceItem?.discount_price || 0,
      );

      if (
        !Number.isFinite(originalPrice) ||
        originalPrice <= 0
      ) {
        return null;
      }

      const hasValidDiscount =
        Number.isFinite(discountPrice) &&
        discountPrice > 0 &&
        discountPrice < originalPrice;

      return hasValidDiscount
        ? discountPrice
        : originalPrice;
    })
    .filter(
      (price) =>
        Number.isFinite(price) &&
        price > 0,
    );

  if (validPrices.length === 0) {
    return null;
  }

  return Math.min(...validPrices);
};

const getMenuItemFoodType = (item) => {
  // -------------------------------------------------------
  // FIRST PRIORITY: ICON
  // API me isVeg inconsistent ho sakta hai.
  // -------------------------------------------------------

  const iconText = Array.isArray(
    item?.icon_dish,
  )
    ? item.icon_dish.join(" ")
    : String(item?.icon_dish || "");

  const normalizedIcon =
    iconText.toLowerCase();

  if (
    normalizedIcon.includes("nonveg") ||
    normalizedIcon.includes("non-veg") ||
    normalizedIcon.includes("non_veg")
  ) {
    return "non-veg";
  }

  if (normalizedIcon.includes("veg")) {
    return "veg";
  }

  // -------------------------------------------------------
  // SECOND PRIORITY: isVeg
  // -------------------------------------------------------

  const rawType = String(
    item?.isVeg ??
      item?.is_veg ??
      "",
  )
    .trim()
    .toLowerCase();

  if (
    [
      "veg",
      "v",
      "vegetarian",
      "yes",
      "y",
      "1",
      "true",
    ].includes(rawType)
  ) {
    return "veg";
  }

  if (
    [
      "nonveg",
      "non-veg",
      "non veg",
      "nonvegetarian",
      "non-vegetarian",
      "n",
      "0",
      "false",
    ].includes(rawType)
  ) {
    return "non-veg";
  }

  return "";
};

// =========================================================
// VEG / NON VEG ICON
// SAME STYLE AS ALL RESTAURANTS
// =========================================================

const FoodTypeIcon = ({ type }) => {
  const normalizedType = String(type || "")
    .trim()
    .toLowerCase();

  const isVeg = normalizedType === "veg";

  const isNonVeg =
    normalizedType === "non-veg" ||
    normalizedType === "nonveg";

  if (!isVeg && !isNonVeg) {
    return null;
  }

  const color = isVeg
    ? "#16a34a"
    : "#dc2626";

  return (
    <div
      className="
        flex
        h-5
        w-5
        items-center
        justify-center
        bg-white/95
        shadow-sm
      "
    >
      <span
        className="
          flex
          h-4
          w-4
          items-center
          justify-center
          border
          bg-white
        "
        style={{
          borderColor: color,
        }}
      >
        <span
          className="
            h-2
            w-2
            rounded-full
          "
          style={{
            backgroundColor: color,
          }}
        />
      </span>
    </div>
  );
};

const getMenuItemTime = (item) => {
  const value = Number(
    item?.estimatedTime ??
      item?.estimated_time ??
      item?.preparationTime ??
      item?.preparation_time ??
      item?.prepTime ??
      item?.prep_time,
  );

  return Number.isFinite(value) &&
    value > 0
    ? value
    : null;
};

const ShimmerStyles = () => (
  <style>
    {`
      @keyframes menuCardShimmerMove {
        0% {
          transform: translateX(-120%);
        }

        100% {
          transform: translateX(120%);
        }
      }

      .menu-card-shimmer {
        position: relative;
        overflow: hidden;
        background: #e8edf3;
      }

      .menu-card-shimmer::after {
        content: "";
        position: absolute;
        inset: 0;
        transform: translateX(-120%);
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.35) 30%,
          rgba(255, 255, 255, 0.95) 50%,
          rgba(255, 255, 255, 0.35) 70%,
          transparent 100%
        );
        animation: menuCardShimmerMove 1.25s linear infinite;
        will-change: transform;
        pointer-events: none;
      }
    `}
  </style>
);

// =========================================================
// MENU ITEM CARD
// =========================================================

const MenuItemCard = ({
  item,
  category,
  cleanUserId,
  cleanOrderType,
  isRestaurantClosed,
  customerId,
  itemLikeUpdatingIds,
  isItemLiked,
  toggleFavourite,
  getQuantity,
  addItem,
  removeItem,
  handleCustomize,
  handleCustomizableAdd,
  openItemDetails,
}) => {
  const itemId = String(item?.item_id || "");

  const quantity = getQuantity(itemId);

  const priceDetails = getPriceDetails(item);

  const priceOptions = priceDetails.priceOptions || [];

  // =========================================================
  // SELECTED SIZE / PRICE
  // =========================================================

  const [selectedPriceIndex, setSelectedPriceIndex] = useState(0);

const selectedPrice =
  priceOptions[selectedPriceIndex] || priceOptions[0] || null;

const hasMultiplePrices = priceOptions.length > 1;

// PEHLE variant banao
const selectedVariantItem = buildSelectedVariantItem(item, selectedPrice);

// PHIR customization check karo
const selectedCustomizations = Array.isArray(selectedPrice?.customizations)
  ? selectedPrice.customizations
  : [];

const variantCustomizations = Array.isArray(
  selectedVariantItem?.prices?.[0]?.customizations,
)
  ? selectedVariantItem.prices[0].customizations
  : [];

const itemCustomizations = Array.isArray(item?.customizations)
  ? item.customizations
  : [];

const hasCustomizations =
  selectedCustomizations.length > 0 ||
  variantCustomizations.length > 0 ||
  itemCustomizations.length > 0;

// Item change hone par first size default active
useEffect(() => {
  setSelectedPriceIndex(0);
}, [itemId]);

const isAvailable = getItemAvailability(item, cleanOrderType);

const foodType = getMenuItemFoodType(item);

const visiblePriceOptions = priceOptions.slice(0, 2);

const remainingPriceOptions = Math.max(priceOptions.length - 2, 0);

  // =========================================================
  // IMAGE
  // =========================================================

  const originalImageSrc = item?.photo || category?.cat_img_url || ChaafoLogo;

  const [imageSrc, setImageSrc] = useState(originalImageSrc);

  const [imageLoading, setImageLoading] = useState(true);

  // =========================================================
  // IMAGE SOURCE CHANGE
  // =========================================================

  useEffect(() => {
    const nextImage = item?.photo || category?.cat_img_url || ChaafoLogo;

    setImageSrc(nextImage);
    setImageLoading(true);
  }, [item?.photo, category?.cat_img_url]);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  // =========================================================
  // FAVOURITE
  // =========================================================

  const isFavourite = isItemLiked({
    shopCode: cleanUserId,
    itemId,
  });

  const isLikeUpdating = itemLikeUpdatingIds.includes(itemId);

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => openItemDetails(item, category, selectedPriceIndex)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();

          openItemDetails(item, category, selectedPriceIndex);
        }
      }}
      className={`group min-w-0 cursor-pointer overflow-hidden rounded-[12px] border border-[#dce2e8] bg-white transition duration-300 sm:rounded-[14px] ${
        isAvailable
          ? "hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(15,23,42,0.08)]"
          : "opacity-70"
      }`}
    >
      <div className="relative aspect-[5/3] w-full overflow-hidden bg-[#eef1f4]">
        {/* IMAGE SHIMMER */}

        {imageLoading && (
          <div className="menu-card-shimmer absolute inset-0 z-[5] h-full w-full" />
        )}

        {/* ACTUAL IMAGE */}

        <img
          src={imageSrc}
          alt={item?.item_name || "Menu item"}
          loading="lazy"
          draggable={false}
          onLoad={handleImageLoad}
          onError={() => {
            if (imageSrc !== ChaafoLogo) {
              setImageLoading(true);
              setImageSrc(ChaafoLogo);
              return;
            }

            setImageLoading(false);
          }}
          className={`
    block
    h-full
    w-full
    object-cover
    bg-white

    transition-opacity
    duration-300

    ${imageLoading ? "opacity-0" : "opacity-100"}
  `}
        />

        {/* VEG / NON VEG ICON - TOP LEFT */}

        {foodType && (
          <div className="absolute left-2 top-2 z-20">
            <FoodTypeIcon type={foodType} />
          </div>
        )}

        {!isAvailable && !imageLoading && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/35">
            <span className="rounded-full bg-white px-2.5 py-1 text-[9px] font-semibold text-gray-800 min-[420px]:px-3 min-[420px]:text-[10px] sm:px-4 sm:py-2 sm:text-[13px]">
              Out of stock
            </span>
          </div>
        )}
      </div>

      {/* =================================================== */}
      {/* CONTENT */}
      {/* =================================================== */}

      <div className="flex min-h-[80px] flex-1 flex-col p-2.5 sm:min-h-[100px] sm:p-3 lg:p-4">
        {/* ITEM NAME */}

        <h3
          title={item?.item_name || ""}
          className="
            truncate
            text-[12px]
            font-semibold
            leading-5
            text-[#202938]

            min-[380px]:text-[13px]
            sm:text-[15px]
            lg:text-[16px]
          "
        >
          {item?.item_name || "Menu item"}
        </h3>

        {/* =================================================== */}
        {/* SIZE OPTIONS - ONLY FIRST 2 ON CARD */}
        {/* =================================================== */}

        {priceOptions.length > 0 && (
          <div className="flex min-w-0 my-2 flex-wrap items-center gap-1 sm:mt-2 sm:gap-1.5">
            {/* FIRST 2 SIZE OPTIONS */}

            {visiblePriceOptions.map((priceOption, index) => {
              const isSelected = selectedPriceIndex === index;

              return (
                <button
                  key={`${itemId}-${priceOption.index}`}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();

                    setSelectedPriceIndex(index);
                  }}
                  className={`
            inline-flex
            items-center
            justify-center
            rounded-full
            border

            px-2
            py-[4px]

            text-[8px]
            font-medium
            leading-none

            transition

            min-[380px]:text-[9px]

            sm:px-2.5
            sm:py-1
            sm:text-[10px]

            ${
              isSelected
                ? `
                    border-[#2454e6]
                    bg-[#eef4ff]
                    font-semibold
                    text-[#2454e6]
                  `
                : `
                    border-[#dce2e8]
                    bg-white
                    text-gray-600

                    hover:border-[#2454e6]
                    hover:text-[#2454e6]
                  `
            }
          `}
                >
                  {priceOption.size}
                </button>
              );
            })}

            {/* ================================================= */}
            {/* MORE OPTIONS */}
            {/* ================================================= */}

            {remainingPriceOptions > 0 && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();

                  openItemDetails(item, category, selectedPriceIndex);
                }}
                className="
          inline-flex
          items-center
          justify-center
          rounded-full
          border
          border-[#dce2e8]
          bg-[#f8fafc]

          px-2
          py-[4px]

          text-[8px]
          font-semibold
          leading-none
          text-[#2454e6]

          transition

          hover:border-[#2454e6]
          hover:bg-[#eef4ff]

          min-[380px]:text-[9px]

          sm:px-2.5
          sm:py-1
          sm:text-[10px]
        "
              >
                +{remainingPriceOptions} More
              </button>
            )}

            {/* ================================================= */}
            {/* CUSTOMIZABLE */}
            {/* ================================================= */}

            {hasCustomizations && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();

                  handleCustomizableAdd(
                    selectedVariantItem,
                    category,
                    quantity,
                  );
                }}
                className="
      inline-flex
      items-center
      rounded-full
      border
      border-blue-200
      bg-blue-50

      px-2
      py-[4px]

      text-[8px]
      font-semibold
      leading-none
      text-blue-700

      transition

      hover:bg-blue-100

      disabled:cursor-not-allowed
      disabled:border-gray-200
      disabled:bg-gray-100
      disabled:text-gray-400

      min-[380px]:text-[9px]

      sm:px-2.5
      sm:py-1
      sm:text-[10px]
    "
              >
                Customizable
              </button>
            )}
          </div>
        )}

        {/* =================================================== */}
        {/* PRICE + ADD */}
        {/* =================================================== */}

        <div className="flex min-w-0 items-center justify-between gap-1 sm:gap-2 sm:pt-1">
          {/* PRICE */}

          <div className="min-w-0 flex-1">
            {selectedPrice ? (
              <div className="flex min-w-0 items-center gap-1.5">
                {/* SELLING PRICE */}

                <span
                  className="
          shrink-0
          whitespace-nowrap
          text-[14px]
          font-bold
          text-[#172033]

          min-[380px]:text-[15px]

          sm:text-[18px]

          lg:text-[19px]
        "
                >
                  {selectedPrice.formattedSellingPrice}
                </span>

                {/* ORIGINAL PRICE */}

                {selectedPrice.hasDiscount && (
                  <span
                    className="
            whitespace-nowrap
            text-[8px]
            text-[#91a4c1]
            line-through

            min-[380px]:text-[9px]

            sm:text-[11px]
          "
                  >
                    {selectedPrice.formattedOriginalPrice}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-[10px] text-gray-400">
                Price unavailable
              </span>
            )}
          </div>

          {/* =================================================== */}
          {/* ADD / QUANTITY */}
          {/* =================================================== */}

          {isAvailable && (
            <div className="shrink-0">
              {quantity === 0 ? (
                <button
                  type="button"
                  disabled={isRestaurantClosed}
                  onClick={(event) => {
                    event.stopPropagation();

                    if (!selectedPrice || isRestaurantClosed) {
                      return;
                    }

                    if (hasCustomizations) {
                      handleCustomizableAdd(
                        selectedVariantItem,
                        category,
                        quantity,
                      );

                      return;
                    }

                    addItem(selectedVariantItem, category);
                  }}
                  className="
  flex
  h-7
  min-w-[54px]
  shrink-0
  items-center
  justify-center

  rounded-full
  bg-[#2454e6]

  px-2.5

  text-[10px]
  font-bold
  text-white

  transition

  hover:bg-[#1d4ed8]
  active:scale-95

  disabled:cursor-not-allowed
  disabled:bg-gray-300
  disabled:text-gray-500
  disabled:hover:bg-gray-300
  disabled:active:scale-100

  min-[380px]:h-8
min-[380px]:min-w-[60px]
min-[380px]:px-3
  min-[380px]:text-[11px]

sm:h-9
sm:min-w-[72px]
sm:px-3.5
  sm:text-[13px]

lg:h-10
lg:min-w-[84px]
lg:px-3
  lg:text-[14px]
"
                >
                  ADD
                </button>
              ) : (
                <div
                  onClick={(event) => event.stopPropagation()}
                  className="
                    flex
                    h-7
                    min-w-[58px]
                    shrink-0
                    items-center
                    overflow-hidden
                    rounded-full
                    bg-[#2454e6]
                    text-white

                    min-[380px]:h-8
                    min-[380px]:min-w-[64px]

                    sm:h-10
                    sm:min-w-0
                  "
                >
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();

                      removeItem(itemId);
                    }}
                    className="
                      flex
                      h-full
                      w-5
                      shrink-0
                      items-center
                      justify-center

                      min-[380px]:w-6

                      sm:w-9
                    "
                  >
                    <Minus className="h-2.5 w-2.5 sm:h-[14px] sm:w-[14px]" />
                  </button>

                  <span
                    className="
                      min-w-[16px]
                      text-center
                      text-[9px]
                      font-semibold

                      min-[380px]:min-w-[18px]
                      min-[380px]:text-[10px]

                      sm:min-w-6
                      sm:text-[12px]
                    "
                  >
                    {quantity}
                  </span>

                  <button
                    type="button"
                    disabled={isRestaurantClosed}
                    onClick={(event) => {
                      event.stopPropagation();

                      if (!selectedPrice || isRestaurantClosed) {
                        return;
                      }

                      if (hasCustomizations) {
                        handleCustomizableAdd(
                          selectedVariantItem,
                          category,
                          quantity,
                        );

                        return;
                      }

                      addItem(selectedVariantItem, category);
                    }}
                    className="
    flex
    h-full
    w-5
    shrink-0
    items-center
    justify-center

    disabled:cursor-not-allowed
    disabled:opacity-40

    min-[380px]:w-6
    sm:w-9
  "
                  >
                    <Plus className="h-2.5 w-2.5 sm:h-[14px] sm:w-[14px]" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};;;;

// =========================================================
// RESPONSIVE MENU STICKY TOP
// =========================================================

const MOBILE_STICKY_TOP = 176;
const TABLET_STICKY_TOP = 154;
const DESKTOP_STICKY_TOP = 82;

const getMenuStickyTop = () => {
  if (typeof window === "undefined") {
    return MOBILE_STICKY_TOP;
  }

  const width = window.innerWidth;

  // Mobile
  if (width < 768) {
    return MOBILE_STICKY_TOP;
  }

  // Tablet / iPad / small laptop
  if (width < 1024) {
    return TABLET_STICKY_TOP;
  }

  // Desktop
  return DESKTOP_STICKY_TOP;
};

export default function RestaurantMenu({
  userid,
  orderType,
  platform,
  onRestaurantLoaded,
  selectedItemName = "",
  isRestaurantClosed: closedByStatus = true,
}) {
  const menuSectionRef = useRef(null);
  const menuPanelRef = useRef(null);
  const menuScrollRef = useRef(null);

  const leftCategoryScrollRef = useRef(null);
  const showcaseRowRefs = useRef({});
  const categoryButtonRefs = useRef({});

  const sectionRefs = useRef({});

  const lastRequestKeyRef = useRef("");

  const categoryClickScrollingRef = useRef(false);
  const clickedCategoryRef = useRef("");

  const scrollAnimationFrameRef = useRef(null);

  const categoryScrollAnimationRef = useRef(null);

  const [isMenuSectionVisible, setIsMenuSectionVisible] = useState(true);

  const [customizationHistory, setCustomizationHistory] = useState({});

  const [repeatCustomizationItem, setRepeatCustomizationItem] = useState(null);

  useEffect(() => {
    const section = menuSectionRef.current;

    if (!section) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsMenuSectionVisible(entry.isIntersecting);
      },
      {
        threshold: 0.01,
      },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  // =========================================================
  // CATALOGUE
  // =========================================================

  const {
    restaurantDetails,
    catalogueMenu,
    getCatalogItems,
    catalogueLoading,
    catalogueError,
  } = useCatalogue();

  // =========================================================
  // GLOBAL CONTEXT
  // =========================================================

  const { user } = useGlobalContext();

  // =========================================================
  // LIKE / DISLIKE
  // =========================================================

  const { insertLikeDislikeItems, isItemLiked } = useRestaurantList();

  const [itemLikeUpdatingIds, setItemLikeUpdatingIds] = useState([]);

  const customerId = String(
    user?.user_code || user?.customer_id || user?.customerId || "",
  ).trim();

  // =========================================================
  // LOCAL STATE
  // =========================================================

  const [activeCategory, setActiveCategory] = useState("");

  const [searchValue, setSearchValue] = useState("");

  const [activeFilter, setActiveFilter] = useState("all");

  const [sortBy, setSortBy] = useState("Sort By");

  const [sortOpen, setSortOpen] = useState(false);

  const sortDropdownRef = useRef(null);
  const resetMenuToFirstRef = useRef(false);

  const menuStickyHeaderRef = useRef(null);
  const [menuStickyHeaderHeight, setMenuStickyHeaderHeight] = useState(0);

  const sortOptions = [
    { value: "Sort By", label: "Sort By" },
    { value: "rating-high", label: "Rating" },
    { value: "price-high", label: "Price: High to Low" },
    { value: "price-low", label: "Price: Low to High" },
    { value: "distance", label: "Distance" },
    { value: "name", label: "Name" },
  ];

  const selectedSortLabel =
    sortOptions.find((option) => option.value === sortBy)?.label || "Sort By";

  const [recommendedItems, setRecommendedItems] = useState([]);

  const [offerItems, setOfferItems] = useState([]);

  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const [cartOpen, setCartOpen] = useState(false);
  const [cartDockTop, setCartDockTop] = useState(null);

  const [selectedCustomizationItem, setSelectedCustomizationItem] =
    useState(null);

  const [selectedItemDetail, setSelectedItemDetail] = useState(null);

  const [selectedDetailPriceIndex, setSelectedDetailPriceIndex] = useState(0);

  const [customizationValues, setCustomizationValues] = useState({});

  const openItemDetails = (item, category, initialPriceIndex = 0) => {
    const totalPrices = Array.isArray(item?.prices) ? item.prices.length : 0;

    const safeIndex =
      initialPriceIndex >= 0 && initialPriceIndex < totalPrices
        ? initialPriceIndex
        : 0;

    setSelectedDetailPriceIndex(safeIndex);

    setSelectedItemDetail({
      ...item,
      category,
    });
  };

  const [menuPinDistance, setMenuPinDistance] = useState(0);

  const [panelPosition, setPanelPosition] = useState("before");

const navigate = useNavigate();

// Status directly received from RestaurantHeroDetails
const isRestaurantClosed = closedByStatus;

  // =========================================================
  // CART
  // =========================================================

  const { items, itemCount, addToCart, decreaseItem, getQuantity } = useCart();

  // =========================================================
  // CUSTOMIZATION HISTORY = CURRENT CART ONLY
  // =========================================================

  useEffect(() => {
    const currentCartHistory = syncCustomizationHistoryWithCart(items);

    setCustomizationHistory(currentCartHistory);

    // Cart completely empty hua to repeat modal bhi close
    if (items.length === 0) {
      setRepeatCustomizationItem(null);
    }
  }, [items]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);
  // =========================================================
  // VIEW CART - STOP BEFORE FOOTER
  // =========================================================

  useEffect(() => {
    const updateCartPosition = () => {
      const footer = document.querySelector('[data-site-footer="true"]');

      const menuSection = menuSectionRef.current;

      const cartButton = document.getElementById("restaurant-view-cart-button");

      if (!footer || !menuSection || !cartButton) {
        setCartDockTop(null);
        return;
      }

      const footerRect = footer.getBoundingClientRect();

      const cartHeight = cartButton.offsetHeight || 48;

      // Same bottom spacing jo fixed button me hai
      let fixedBottomGap = 8;

      if (window.innerWidth >= 1280) {
        fixedBottomGap = 24;
      } else if (window.innerWidth >= 1024) {
        fixedBottomGap = 20;
      } else if (window.innerWidth >= 768) {
        fixedBottomGap = 16;
      } else if (window.innerWidth >= 640) {
        fixedBottomGap = 12;
      }

      // Footer aur View Cart ke beech gap
      const footerGap = 8;

      const fixedCartBottom = window.innerHeight - fixedBottomGap;

      /*
       * Footer abhi View Cart tak nahi aaya:
       * button normal fixed rahe.
       */
      if (footerRect.top > fixedCartBottom + footerGap) {
        setCartDockTop(null);
        return;
      }

      /*
       * Footer aa gaya:
       * View Cart ko footer ke just upper
       * ek permanent document position par dock karo.
       */
      const footerAbsoluteTop = window.scrollY + footerRect.top;

      const sectionRect = menuSection.getBoundingClientRect();

      const sectionAbsoluteTop = window.scrollY + sectionRect.top;

      const nextDockTop =
        footerAbsoluteTop - sectionAbsoluteTop - cartHeight - footerGap;

      const roundedDockTop = Math.round(nextDockTop);

      setCartDockTop((previous) =>
        previous === roundedDockTop ? previous : roundedDockTop,
      );
    };

    updateCartPosition();

    window.addEventListener("scroll", updateCartPosition, {
      passive: true,
    });

    window.addEventListener("resize", updateCartPosition);

    return () => {
      window.removeEventListener("scroll", updateCartPosition);

      window.removeEventListener("resize", updateCartPosition);
    };
  }, []);

  // =========================================================
  // NORMALIZED VALUES
  // =========================================================

  const cleanUserId = String(userid || "").trim();

  const cleanOrderType = "home_delivery";

  const cleanPlatform = String(platform || "").trim();

  // =========================================================
  // LOCK BODY WHEN MODAL OPEN
  // =========================================================

  useEffect(() => {
    if (!selectedItemDetail && !selectedCustomizationItem) {
      return undefined;
    }

    const oldOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = oldOverflow;
    };
  }, [selectedItemDetail, selectedCustomizationItem]);

  // =========================================================
  // ESCAPE KEY
  // =========================================================

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      if (selectedCustomizationItem) {
        setSelectedCustomizationItem(null);
        setCustomizationValues({});

        return;
      }

      if (selectedItemDetail) {
        setSelectedItemDetail(null);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [selectedCustomizationItem, selectedItemDetail]);

  // =========================================================
  // FETCH CATALOGUE
  // =========================================================

  const fetchCatalogue = useCallback(
    async (force = false) => {
      if (!cleanUserId) {
        console.error("Catalogue API not called because userid is missing.", {
          userid,
          orderType,
          platform,
        });

        return;
      }

      const requestKey = [cleanUserId, cleanOrderType, cleanPlatform].join("|");

      if (!force && lastRequestKeyRef.current === requestKey) {
        return;
      }

      lastRequestKeyRef.current = requestKey;

      const result = await getCatalogItems({
        userid: cleanUserId,
        orderType: cleanOrderType,
        platform: cleanPlatform,
        forceRefresh: force,
      });

      setRecommendedItems(
        Array.isArray(result?.recommendedItems) ? result.recommendedItems : [],
      );

      setOfferItems(Array.isArray(result?.offerItems) ? result.offerItems : []);

      if (!result?.success) {
        lastRequestKeyRef.current = "";
      }
    },
    [
      cleanUserId,
      cleanOrderType,
      cleanPlatform,
      userid,
      orderType,
      selectedItemName,
      platform,
      getCatalogItems,
      isRestaurantClosed,
    ],
  );

  useEffect(() => {
    fetchCatalogue();
  }, [fetchCatalogue]);

  // =========================================================
  // RESTAURANT CALLBACK
  // =========================================================

  useEffect(() => {
    if (restaurantDetails && typeof onRestaurantLoaded === "function") {
      onRestaurantLoaded(restaurantDetails);
    }
  }, [restaurantDetails, onRestaurantLoaded]);

  // =========================================================
  // CLEANUP
  // =========================================================

  useEffect(() => {
    return () => {
      if (scrollAnimationFrameRef.current) {
        window.cancelAnimationFrame(scrollAnimationFrameRef.current);
      }

      if (categoryScrollAnimationRef.current) {
        window.cancelAnimationFrame(categoryScrollAnimationRef.current);
      }
    };
  }, []);

  // =========================================================
  // SEARCH + SORT
  // =========================================================
  const filteredCategories = useMemo(() => {
    const categories = Array.isArray(catalogueMenu) ? catalogueMenu : [];

    const normalizeText = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

    const query = normalizeText(searchValue);

    // =======================================================
    // SEARCH RANK
    // =======================================================

    const getSearchRank = (text) => {
      if (!query) {
        return 999;
      }

      const cleanText = normalizeText(text);

      if (!cleanText) {
        return 999;
      }

      if (cleanText === query) {
        return 0;
      }

      if (cleanText.startsWith(query)) {
        return 1;
      }

      if (cleanText.includes(query)) {
        return 2;
      }

      return 999;
    };

    // =======================================================
    // PREPARE CATEGORIES
    // =======================================================

    const preparedCategories = categories
      .map((category, categoryIndex) => {
        const originalMenuItems = Array.isArray(category?.menu_items)
          ? category.menu_items
          : [];

        const categorySearchRank = getSearchRank(category?.category_name);

        // =================================================
        // PREPARE ITEMS
        // =================================================

        let preparedItems = originalMenuItems.map((item, itemIndex) => {
          return {
            item,
            itemIndex,

            searchRank: getSearchRank(item?.item_name),

            isAvailable: getItemAvailability(item, cleanOrderType),

            rating: getMenuItemRating(item),

            price: getMenuItemStartingPrice(item),

            foodType: getMenuItemFoodType(item),

            estimatedTime: getMenuItemTime(item),
          };
        });

        // =================================================
        // ACTIVE FILTER
        // =================================================

        if (activeFilter === "top-rated") {
          preparedItems = preparedItems.filter(({ rating }) => rating >= 4.5);
        }

        if (activeFilter === "under-30") {
          preparedItems = preparedItems.filter(
            ({ estimatedTime }) =>
              Number.isFinite(estimatedTime) &&
              estimatedTime > 0 &&
              estimatedTime <= 30,
          );
        }

        if (activeFilter === "150-300") {
          preparedItems = preparedItems.filter(
            ({ price }) =>
              Number.isFinite(price) && price >= 150 && price <= 300,
          );
        }

        if (activeFilter === "above-300") {
          preparedItems = preparedItems.filter(
            ({ price }) => Number.isFinite(price) && price > 300,
          );
        }

        if (activeFilter === "pure-veg") {
          preparedItems = preparedItems.filter(
            ({ foodType }) => foodType === "veg",
          );
        }

        if (activeFilter === "non-veg") {
          preparedItems = preparedItems.filter(
            ({ foodType }) => foodType === "non-veg",
          );
        }

        // =================================================
        // BEST SEARCH MATCH
        // =================================================

        const bestItemSearchRank = preparedItems.reduce(
          (bestRank, currentItem) => Math.min(bestRank, currentItem.searchRank),
          999,
        );

        const categoryFinalSearchRank =
          bestItemSearchRank < 999
            ? bestItemSearchRank
            : categorySearchRank < 999
              ? categorySearchRank + 10
              : 999;

        // =================================================
        // ITEM SORT
        // =================================================

        preparedItems.sort((first, second) => {
          // ---------------------------------------------
          // SEARCH MATCH FIRST
          // ---------------------------------------------

          if (query && first.searchRank !== second.searchRank) {
            return first.searchRank - second.searchRank;
          }

          // ---------------------------------------------
          // RATING
          // ---------------------------------------------

          if (sortBy === "rating-high") {
            const difference = second.rating - first.rating;

            if (difference !== 0) {
              return difference;
            }
          }

          // ---------------------------------------------
          // PRICE LOW TO HIGH
          // ---------------------------------------------

          if (sortBy === "price-low") {
            const firstPrice = first.price ?? Infinity;

            const secondPrice = second.price ?? Infinity;

            if (firstPrice !== secondPrice) {
              return firstPrice - secondPrice;
            }
          }

          // ---------------------------------------------
          // PRICE HIGH TO LOW
          // ---------------------------------------------

          if (sortBy === "price-high") {
            const firstPrice = first.price ?? -1;

            const secondPrice = second.price ?? -1;

            if (firstPrice !== secondPrice) {
              return secondPrice - firstPrice;
            }
          }

          // ---------------------------------------------
          // NAME
          // ---------------------------------------------

          if (sortBy === "name") {
            const nameDifference = String(
              first.item?.item_name || "",
            ).localeCompare(String(second.item?.item_name || ""));

            if (nameDifference !== 0) {
              return nameDifference;
            }
          }

          // ---------------------------------------------
          // DEFAULT API ORDER
          // ---------------------------------------------

          return first.itemIndex - second.itemIndex;
        });

        const sortedItems = preparedItems.map(({ item }) => item);

        // =================================================
        // CATEGORY STOCK STATUS
        // =================================================

        const allItemsOutOfStock =
          sortedItems.length > 0 &&
          sortedItems.every(
            (item) => !getItemAvailability(item, cleanOrderType),
          );

        return {
          ...category,

          menu_items: sortedItems,

          _searchRank: categoryFinalSearchRank,

          _allItemsOutOfStock: allItemsOutOfStock,

          _originalCategoryIndex: categoryIndex,
        };
      })

      // Filter lagne ke baad jis category me 0 items hain
      // wo left category list me bhi nahi dikhegi.
      .filter(
        (category) =>
          Array.isArray(category?.menu_items) && category.menu_items.length > 0,
      );

    // =======================================================
    // CATEGORY SORT - API POSITION
    // =======================================================

    const getCategoryPosition = (category) => {
      const rawPosition = String(category?.position ?? "").trim();

      // Empty, null, undefined or invalid position
      if (!/^\d+$/.test(rawPosition)) {
        return null;
      }

      return Number(rawPosition);
    };

    return [...preparedCategories].sort((first, second) => {
      // Search active hai toh best matching category first
      if (query && first._searchRank !== second._searchRank) {
        return first._searchRank - second._searchRank;
      }

      const firstPosition = getCategoryPosition(first);
      const secondPosition = getCategoryPosition(second);

      // Both have valid positions
      if (firstPosition !== null && secondPosition !== null) {
        if (firstPosition !== secondPosition) {
          return firstPosition - secondPosition;
        }
      }

      // Valid position wali category pehle
      if (firstPosition !== null && secondPosition === null) {
        return -1;
      }

      if (firstPosition === null && secondPosition !== null) {
        return 1;
      }

      // Position empty ya same hai toh original API sequence
      return first._originalCategoryIndex - second._originalCategoryIndex;
    });
  }, [catalogueMenu, searchValue, cleanOrderType, activeFilter, sortBy]);

  const isMenuResultEmpty =
    !catalogueLoading && !catalogueError && filteredCategories.length === 0;

  // =========================================================
  // BEST FOR YOU
  // =========================================================

  const bestForYouItems = useMemo(() => {
    const cleanBestFor = String(selectedItemName || "")
      .trim()
      .toLowerCase();

    if (!cleanBestFor) {
      return [];
    }

    const categories = Array.isArray(catalogueMenu) ? catalogueMenu : [];

    const matchedItems = [];

    categories.forEach((category) => {
      const menuItems = Array.isArray(category?.menu_items)
        ? category.menu_items
        : [];

      menuItems.forEach((item) => {
        const itemName = String(item?.item_name || "")
          .trim()
          .toLowerCase();

        const isMatched = itemName.includes(cleanBestFor);

        const isAvailable = getItemAvailability(item, cleanOrderType);

        if (isMatched && isAvailable) {
          matchedItems.push({
            item,
            category,
          });
        }
      });
    });

    return matchedItems;
  }, [catalogueMenu, selectedItemName, cleanOrderType]);

  // =========================================================
  // TOP SHOWCASE SECTIONS
  // Best For You / Recommended / Offers
  // =========================================================

  const showcaseSections = useMemo(() => {
    const sections = [];

    if (bestForYouItems.length > 0) {
      sections.push({
        id: "best-for-you",

        title: selectedItemName
          ? `Top picks for ${selectedItemName}`
          : "Best For You",

        subtitle: `${bestForYouItems.length} ${
          bestForYouItems.length === 1 ? "item" : "items"
        } selected for you`,

        items: bestForYouItems.map(({ item, category }) => ({
          item,
          category,
        })),
      });
    }

    if (recommendedItems.length > 0) {
      sections.push({
        id: "recommended",

        title: "Recommended",

        subtitle: "Popular choices from this restaurant",

        items: recommendedItems.map((item) => ({
          item,

          category: item?.recommendationCategory || {},
        })),
      });
    }

    if (offerItems.length > 0) {
      sections.push({
        id: "offers",

        title: "Offers",

        subtitle: "Special offers available right now",

        items: offerItems.map((item) => ({
          item,

          category: item?.offerCategory || {},
        })),
      });
    }

    return sections;
  }, [bestForYouItems, recommendedItems, offerItems, selectedItemName]);

  const showShowcaseSections =
    !searchValue.trim() && showcaseSections.length > 0;

  // =========================================================
  // ACTIVE CATEGORY
  // =========================================================

  useEffect(() => {
    if (filteredCategories.length === 0) {
      setActiveCategory("");

      return;
    }

    const activeCategoryExists = filteredCategories.some(
      (category) => String(category?.cat_id) === String(activeCategory),
    );

    if (!activeCategoryExists) {
      const firstCategoryId = String(filteredCategories[0]?.cat_id || "");

      setActiveCategory(firstCategoryId);

      if (menuScrollRef.current) {
        menuScrollRef.current.scrollTop = 0;
      }
    }
  }, [filteredCategories, activeCategory]);

  // =========================================================
  // SEARCH CHANGE
  // MATCHED CATEGORY + ITEM KO ACTUALLY TOP PAR DIKHAO
  // =========================================================

  useEffect(() => {
    const query = searchValue.trim().toLowerCase();

    if (!query || filteredCategories.length === 0) {
      return;
    }

    const firstCategoryId = String(filteredCategories[0]?.cat_id || "");

    if (!firstCategoryId) {
      return;
    }

    setActiveCategory(firstCategoryId);

    const frame = window.requestAnimationFrame(() => {
      // RIGHT LIST TOP
      if (menuScrollRef.current) {
        menuScrollRef.current.scrollTop = 0;
      }

      // LEFT CATEGORY TOP
      if (leftCategoryScrollRef.current) {
        leftCategoryScrollRef.current.scrollTop = 0;
      }

      /*
       * Tumhara menu internal scroll window scroll ke
       * saath synced hai.
       *
       * Isliye window ko bhi menu ke start par align
       * karna zaroori hai, warna updatePinnedMenu()
       * old scroll position dubara laga deta hai.
       */
      const menuSection = menuSectionRef.current;

      if (menuSection) {
        const sectionAbsoluteTop =
          window.scrollY + menuSection.getBoundingClientRect().top;

        const pinStart = sectionAbsoluteTop - getMenuStickyTop();

        window.scrollTo({
          top: Math.max(0, pinStart),
          behavior: "auto",
        });
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [searchValue, filteredCategories]);

  // =========================================================
  // DYNAMIC STICKY HEADER HEIGHT
  // =========================================================

  useEffect(() => {
    const header = menuStickyHeaderRef.current;

    if (!header) return;

    const updateHeight = () => {
      const height = Math.ceil(header.getBoundingClientRect().height);

      setMenuStickyHeaderHeight((previous) =>
        previous === height ? previous : height,
      );
    };

    updateHeight();

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateHeight)
        : null;

    observer?.observe(header);

    window.addEventListener("resize", updateHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [catalogueLoading, catalogueError, catalogueMenu?.length]);

  // =========================================================
  // MENU SCROLL
  // =========================================================

  const handleMenuScroll = useCallback(() => {
    if (categoryClickScrollingRef.current) {
      return;
    }

    const scrollContainer = menuScrollRef.current;

    if (!scrollContainer || filteredCategories.length === 0) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();

    // Visible area ke upper 30% point ko active section maanenge
    const activationPoint = containerRect.top + containerRect.height * 0.3;

    let currentCategory = "";

    for (const category of filteredCategories) {
      const categoryId = String(category?.cat_id || "");
      const section = sectionRefs.current[categoryId];

      if (!section) continue;

      const sectionRect = section.getBoundingClientRect();

      // Jo section activation point ko cover kar raha hai
      if (
        sectionRect.top <= activationPoint &&
        sectionRect.bottom > activationPoint
      ) {
        currentCategory = categoryId;
        break;
      }
    }

    // First section ke start par
    if (!currentCategory) {
      const firstCategory = filteredCategories[0];
      const firstCategoryId = String(firstCategory?.cat_id || "");
      const firstSection = sectionRefs.current[firstCategoryId];

      if (firstSection) {
        const firstRect = firstSection.getBoundingClientRect();

        if (firstRect.top >= containerRect.top) {
          currentCategory = firstCategoryId;
        }
      }
    }

    // Last section / bottom
    const reachedBottom =
      scrollContainer.scrollTop + scrollContainer.clientHeight >=
      scrollContainer.scrollHeight - 10;

    if (reachedBottom) {
      currentCategory = String(
        filteredCategories[filteredCategories.length - 1]?.cat_id || "",
      );
    }

    if (currentCategory) {
      setActiveCategory(currentCategory);
    }
  }, [filteredCategories]);

  // =========================================================
  // RELEASE CATEGORY LOCK ONLY ON REAL USER SCROLL
  // =========================================================

  useEffect(() => {
    const releaseCategoryLock = () => {
      if (!categoryClickScrollingRef.current) {
        return;
      }

      categoryClickScrollingRef.current = false;

      clickedCategoryRef.current = "";
    };

    const handleScrollKey = (event) => {
      const scrollKeys = [
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        " ",
      ];

      if (scrollKeys.includes(event.key)) {
        releaseCategoryLock();
      }
    };

    window.addEventListener("wheel", releaseCategoryLock, {
      passive: true,
    });

    window.addEventListener("touchmove", releaseCategoryLock, {
      passive: true,
    });

    window.addEventListener("keydown", handleScrollKey);

    return () => {
      window.removeEventListener("wheel", releaseCategoryLock);

      window.removeEventListener("touchmove", releaseCategoryLock);

      window.removeEventListener("keydown", handleScrollKey);
    };
  }, []);

  // =========================================================
  // AUTO SCROLL LEFT CATEGORY TO ACTIVE ITEM
  // =========================================================

  useEffect(() => {
    if (!activeCategory) {
      return;
    }

    const leftContainer = leftCategoryScrollRef.current;

    const activeButton = categoryButtonRefs.current[String(activeCategory)];

    if (!leftContainer || !activeButton) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      const containerRect = leftContainer.getBoundingClientRect();

      const buttonRect = activeButton.getBoundingClientRect();

      const topSafeArea = 12;
      const bottomSafeArea = 12;

      const isAboveVisibleArea =
        buttonRect.top < containerRect.top + topSafeArea;

      const isBelowVisibleArea =
        buttonRect.bottom > containerRect.bottom - bottomSafeArea;

      // Already visible hai to unnecessary scroll nahi hoga
      if (!isAboveVisibleArea && !isBelowVisibleArea) {
        return;
      }

      const buttonOffsetInsideContainer = buttonRect.top - containerRect.top;

      const targetScrollTop =
        leftContainer.scrollTop +
        buttonOffsetInsideContainer -
        leftContainer.clientHeight / 2 +
        buttonRect.height / 2;

      const maxScrollTop = Math.max(
        0,
        leftContainer.scrollHeight - leftContainer.clientHeight,
      );

      const finalScrollTop = Math.min(
        maxScrollTop,
        Math.max(0, targetScrollTop),
      );

      leftContainer.scrollTo({
        top: finalScrollTop,
        behavior: "smooth",
      });
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [activeCategory]);

  // =========================================================
  // CALCULATE PIN DISTANCE
  // =========================================================

  const calculateMenuPinDistance = useCallback(() => {
    const scrollContainer = menuScrollRef.current;

    if (
      !scrollContainer ||
      catalogueLoading ||
      catalogueError ||
      filteredCategories.length === 0
    ) {
      setMenuPinDistance(0);

      return 0;
    }

    const maximumInternalScroll = Math.max(
      0,
      scrollContainer.scrollHeight - scrollContainer.clientHeight,
    );

    setMenuPinDistance((previous) => {
      if (Math.abs(previous - maximumInternalScroll) < 1) {
        return previous;
      }

      return maximumInternalScroll;
    });

    return maximumInternalScroll;
  }, [catalogueLoading, catalogueError, filteredCategories]);

  // =========================================================
  // UPDATE PINNED MENU
  // =========================================================

  const updatePinnedMenu = useCallback(() => {
    // Category click animation khud window + menu dono sync karegi.
    // Is time normal pinned sync ko interfere nahi karne dena.
    if (categoryClickScrollingRef.current) {
      return;
    }

    const menuSection = menuSectionRef.current;
    const scrollContainer = menuScrollRef.current;

    if (!menuSection || !scrollContainer || filteredCategories.length === 0) {
      if (filteredCategories.length === 0) {
        setPanelPosition("before");

        if (scrollContainer?.scrollTop) {
          scrollContainer.scrollTop = 0;
        }
      }

      return;
    }

    const stickyTop = getMenuStickyTop();

    const sectionAbsoluteTop =
      window.scrollY + menuSection.getBoundingClientRect().top;

    const pinStart = sectionAbsoluteTop - stickyTop;

    const maximumInternalScroll = Math.max(
      0,
      scrollContainer.scrollHeight - scrollContainer.clientHeight,
    );

    const pinEnd = pinStart + maximumInternalScroll;

    const currentWindowScroll = window.scrollY;

    // ============================================
    // BEFORE MENU PIN
    // ============================================
    if (currentWindowScroll < pinStart) {
      setPanelPosition((previous) =>
        previous === "before" ? previous : "before",
      );

      if (scrollContainer.scrollTop !== 0) {
        scrollContainer.scrollTop = 0;
      }

      return;
    }

    // ============================================
    // MENU FINISHED
    // ============================================
    if (maximumInternalScroll === 0 || currentWindowScroll >= pinEnd) {
      if (Math.abs(scrollContainer.scrollTop - maximumInternalScroll) > 1) {
        scrollContainer.scrollTop = maximumInternalScroll;
      }

      setPanelPosition((previous) =>
        previous === "after" ? previous : "after",
      );

      return;
    }

    // ============================================
    // MENU PINNED
    // ============================================
    setPanelPosition((previous) => (previous === "fixed" ? previous : "fixed"));

    const nextInternalScroll = Math.max(
      0,
      Math.min(maximumInternalScroll, currentWindowScroll - pinStart),
    );

    if (Math.abs(scrollContainer.scrollTop - nextInternalScroll) > 1) {
      scrollContainer.scrollTop = nextInternalScroll;
    }
  }, [filteredCategories.length]);

  // =========================================================
  // RESIZE OBSERVER
  // =========================================================

  useEffect(() => {
    if (catalogueLoading || catalogueError || filteredCategories.length === 0) {
      setMenuPinDistance(0);
      setPanelPosition("before");

      return undefined;
    }

    const calculateAndUpdate = () => {
      calculateMenuPinDistance();
      updatePinnedMenu();
    };

    const initialFrame = window.requestAnimationFrame(calculateAndUpdate);

    const delayedTimer = window.setTimeout(calculateAndUpdate, 150);

    const scrollContainer = menuScrollRef.current;

    const scrollContent = scrollContainer?.firstElementChild || null;

    let resizeObserver = null;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(calculateAndUpdate);

      if (scrollContainer) {
        resizeObserver.observe(scrollContainer);
      }

      if (scrollContent) {
        resizeObserver.observe(scrollContent);
      }

      if (menuPanelRef.current) {
        resizeObserver.observe(menuPanelRef.current);
      }
    }

    window.addEventListener("resize", calculateAndUpdate);

    return () => {
      window.cancelAnimationFrame(initialFrame);

      window.clearTimeout(delayedTimer);

      resizeObserver?.disconnect();

      window.removeEventListener("resize", calculateAndUpdate);
    };
  }, [
    catalogueLoading,
    catalogueError,
    filteredCategories,
    calculateMenuPinDistance,
    updatePinnedMenu,
  ]);

  // =========================================================
  // WINDOW SCROLL
  // =========================================================

  useEffect(() => {
    const handleWindowScroll = () => {
      if (scrollAnimationFrameRef.current) {
        return;
      }

      scrollAnimationFrameRef.current = window.requestAnimationFrame(() => {
        scrollAnimationFrameRef.current = null;

        updatePinnedMenu();
      });
    };

    window.addEventListener("scroll", handleWindowScroll, {
      passive: true,
    });

    updatePinnedMenu();

    return () => {
      window.removeEventListener("scroll", handleWindowScroll);

      if (scrollAnimationFrameRef.current) {
        window.cancelAnimationFrame(scrollAnimationFrameRef.current);

        scrollAnimationFrameRef.current = null;
      }
    };
  }, [updatePinnedMenu, menuPinDistance]);

  // =========================================================
  // CATEGORY CLICK
  // =========================================================

  const handleCategoryClick = (categoryId) => {
    const cleanCategoryId = String(categoryId);

    const menuSection = menuSectionRef.current;
    const scrollContainer = menuScrollRef.current;
    const categorySection = sectionRefs.current[cleanCategoryId];

    if (!menuSection || !scrollContainer || !categorySection) {
      return;
    }

    // Previous category animation stop
    if (categoryScrollAnimationRef.current) {
      window.cancelAnimationFrame(categoryScrollAnimationRef.current);

      categoryScrollAnimationRef.current = null;
    }

    categoryClickScrollingRef.current = true;
    clickedCategoryRef.current = cleanCategoryId;

    setActiveCategory(cleanCategoryId);

    // =========================================================
    // TARGET INTERNAL MENU POSITION
    // =========================================================

    const containerRect = scrollContainer.getBoundingClientRect();

    const sectionRect = categorySection.getBoundingClientRect();

    // Sticky Menu header ki actual height
    const stickyMenuHeader = scrollContainer.querySelector(
      '[data-menu-sticky-header="true"]',
    );

    const stickyHeaderHeight =
      stickyMenuHeader?.getBoundingClientRect().height || 0;

    // Selected category ka TOP sticky header ke just neeche aayega
    const targetInternalScroll =
      scrollContainer.scrollTop +
      sectionRect.top -
      containerRect.top -
      stickyHeaderHeight -
      8;

    const maximumInternalScroll = Math.max(
      0,
      scrollContainer.scrollHeight - scrollContainer.clientHeight,
    );

    const normalizedInternalScroll = Math.min(
      maximumInternalScroll,
      Math.max(0, targetInternalScroll),
    );

    // =========================================================
    // TARGET WINDOW POSITION
    // =========================================================

    const sectionAbsoluteTop =
      window.scrollY + menuSection.getBoundingClientRect().top;

    const pinStart = sectionAbsoluteTop - getMenuStickyTop();

    const pinEnd = pinStart + maximumInternalScroll;

    const targetWindowScroll = pinStart + normalizedInternalScroll;

    const startWindowScroll = window.scrollY;

    const startInternalScroll = scrollContainer.scrollTop;

    const windowDistance = targetWindowScroll - startWindowScroll;

    const internalDistance = normalizedInternalScroll - startInternalScroll;

    // Long category jump bhi controlled duration me hoga
    const totalDistance = Math.abs(windowDistance);

    const duration = Math.min(650, Math.max(320, 320 + totalDistance * 0.12));

    const startTime = performance.now();

    // Smooth ease-in-out
    const easeInOutCubic = (progress) => {
      return progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
    };

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;

      const progress = Math.min(elapsed / duration, 1);

      const easedProgress = easeInOutCubic(progress);

      const nextWindowScroll =
        startWindowScroll + windowDistance * easedProgress;

      const nextInternalScroll =
        startInternalScroll + internalDistance * easedProgress;

      // =====================================================
      // IMPORTANT FIX
      // Category click ke time normal updatePinnedMenu locked hai.
      // Isliye panel position yahin manually sync karni hai.
      // =====================================================

      let nextPanelPosition = "fixed";

      if (nextWindowScroll < pinStart) {
        nextPanelPosition = "before";
      } else if (nextWindowScroll >= pinEnd) {
        nextPanelPosition = "after";
      }

      setPanelPosition((previous) =>
        previous === nextPanelPosition ? previous : nextPanelPosition,
      );

      // Window + right menu SAME FRAME me move karenge
      window.scrollTo(0, nextWindowScroll);

      scrollContainer.scrollTop = nextInternalScroll;

      if (progress < 1) {
        categoryScrollAnimationRef.current =
          window.requestAnimationFrame(animateScroll);

        return;
      }

      // =========================================================
      // EXACT FINAL POSITION
      // =========================================================

      window.scrollTo(0, targetWindowScroll);

      scrollContainer.scrollTop = normalizedInternalScroll;

      // Final panel position bhi exact rakho
      const finalPanelPosition =
        targetWindowScroll >= pinEnd
          ? "after"
          : targetWindowScroll < pinStart
            ? "before"
            : "fixed";

      setPanelPosition((previous) =>
        previous === finalPanelPosition ? previous : finalPanelPosition,
      );

      categoryScrollAnimationRef.current = null;

      // =========================================================
      // SELECTED CATEGORY LOCK
      // =========================================================

      const finalSelectedCategory = clickedCategoryRef.current;

      setActiveCategory(finalSelectedCategory);

      // Existing behaviour same:
      // user manually scroll kare tabhi lock release hoga.
      categoryClickScrollingRef.current = true;
    };

    categoryScrollAnimationRef.current =
      window.requestAnimationFrame(animateScroll);
  };

  // =========================================================
  // VIEW ALL -> ALWAYS OPEN FROM FIRST CATEGORY
  // =========================================================

  useEffect(() => {
    if (!resetMenuToFirstRef.current) {
      return;
    }

    if (activeFilter !== "all") {
      return;
    }

    if (filteredCategories.length === 0) {
      return;
    }

    const firstCategoryId = String(filteredCategories[0]?.cat_id || "");

    if (!firstCategoryId) {
      resetMenuToFirstRef.current = false;
      return;
    }

    const firstFrame = window.requestAnimationFrame(() => {
      const secondFrame = window.requestAnimationFrame(() => {
        resetMenuToFirstRef.current = false;

        // LEFT CATEGORY FIRST POSITION
        if (leftCategoryScrollRef.current) {
          leftCategoryScrollRef.current.scrollTop = 0;
        }

        // RIGHT MENU FIRST POSITION
        if (menuScrollRef.current) {
          menuScrollRef.current.scrollTop = 0;
        }

        // FIRST CATEGORY ACTIVE
        setActiveCategory(firstCategoryId);

        // FIRST CATEGORY KO EXACT TOP PAR ALIGN
        handleCategoryClick(firstCategoryId);
      });

      resetMenuToFirstRef.currentFrame = secondFrame;
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);

      if (resetMenuToFirstRef.currentFrame) {
        window.cancelAnimationFrame(resetMenuToFirstRef.currentFrame);
      }
    };
  }, [activeFilter, filteredCategories]);

  // =========================================================
  // FOOTER SCROLL TO TOP - FIX MENU SCROLL SYNC
  // =========================================================

  useEffect(() => {
    const handleScrollToTop = () => {
      // Stop any running category scroll animation
      if (categoryScrollAnimationRef.current !== null) {
        window.cancelAnimationFrame(categoryScrollAnimationRef.current);
        categoryScrollAnimationRef.current = null;
      }

      // Release category selection scroll lock
      categoryClickScrollingRef.current = false;
      clickedCategoryRef.current = "";

      // Internal menu scroll will now follow window scroll
      // automatically through updatePinnedMenu().
    };

    window.addEventListener("chaafo:scroll-to-top", handleScrollToTop);

    return () => {
      window.removeEventListener("chaafo:scroll-to-top", handleScrollToTop);
    };
  }, []);

  // =========================================================
  // RETRY
  // =========================================================

  const handleRetry = () => {
    lastRequestKeyRef.current = "";

    fetchCatalogue(true);
  };

  // =========================================================
  // FAVOURITE
  // =========================================================

  const toggleFavourite = async (itemId) => {
    const cleanItemId = String(itemId || "").trim();

    if (!customerId) {
      console.error("Please login to favourite an item.");

      return;
    }

    if (!cleanUserId) {
      console.error("Shop code is missing.");

      return;
    }

    if (!cleanItemId) {
      console.error("Item ID is missing.");

      return;
    }

    if (itemLikeUpdatingIds.includes(cleanItemId)) {
      return;
    }

    const currentlyLiked = isItemLiked({
      shopCode: cleanUserId,
      itemId: cleanItemId,
    });

    setItemLikeUpdatingIds((previous) => [...previous, cleanItemId]);

    try {
      const result = await insertLikeDislikeItems({
        customerId,
        shopCode: cleanUserId,
        itemId: cleanItemId,
        itemStatus: currentlyLiked ? "N" : "Y",
      });

      if (!result?.success) {
        console.error(result?.message || "Unable to update item favourite.");
      }
    } finally {
      setItemLikeUpdatingIds((previous) =>
        previous.filter((id) => id !== cleanItemId),
      );
    }
  };

  // =========================================================
  // ADD ITEM
  // =========================================================

  const addItem = (item, category, selectedCustomizations = {}) => {
    if (isRestaurantClosed) {
      return;
    }

    addToCart({
      item,

      category,

      restaurant: {
        ...restaurantDetails,
        userid: cleanUserId,
      },

      orderType: cleanOrderType,

      selectedCustomizations,
    });
  };

  // =========================================================
  // REMOVE ITEM
  // =========================================================

  const removeItem = (itemId) => {
    decreaseItem(itemId);
  };

  // =========================================================
  // CUSTOMIZATION
  // =========================================================

  const handleCustomize = (item, category) => {
    setSelectedCustomizationItem({
      ...item,
      category,
    });

    setCustomizationValues({});
  };

  // =========================================================
  // CUSTOMIZABLE ADD / PLUS
  // =========================================================

  const handleCustomizableAdd = (item, category, currentQuantity = 0) => {
    // =====================================================
    // RESTAURANT CLOSED
    // Hamesha normal customization modal open hoga
    // Cart me add nahi hoga because Done disabled hai
    // =====================================================
    if (isRestaurantClosed) {
      handleCustomize(item, category);
      return;
    }

    // =====================================================
    // CART EMPTY / ITEM NOT IN CART
    // Old preference modal kabhi open nahi hoga
    // =====================================================

    if (Number(itemCount || 0) <= 0 || Number(currentQuantity || 0) <= 0) {
      setRepeatCustomizationItem(null);

      handleCustomize(item, category);

      return;
    }

    const historyKey = buildCustomizationHistoryKey(cleanUserId, item);

    const savedPreferences = Array.isArray(customizationHistory?.[historyKey])
      ? customizationHistory[historyKey]
      : [];

    if (savedPreferences.length === 0) {
      handleCustomize(item, category);
      return;
    }

    setRepeatCustomizationItem({
      item,
      category,
      historyKey,
      preferences: savedPreferences,
    });
  };

  // =========================================================
  // REPEAT OLD PREFERENCE
  // =========================================================

  const handleRepeatCustomization = (preference) => {
    if (!repeatCustomizationItem) {
      return;
    }

    addItem(
      repeatCustomizationItem.item,
      repeatCustomizationItem.category,
      preference,
    );

    setRepeatCustomizationItem(null);
  };

  // =========================================================
  // CHECKOUT
  // =========================================================

  const handleCheckout = () => {
    setCartOpen(false);

    navigate("/checkout?startCart=1");
  };

  // =========================================================
  // PANEL STYLE
  // =========================================================

  const getPanelStyle = () => {
    // Filter/search se koi item nahi mila:
    // normal document flow me rakho.
    if (isMenuResultEmpty) {
      return {
        position: "relative",
        width: "100%",
        zIndex: 60,
      };
    }

    if (panelPosition === "fixed") {
      return {
        position: "fixed",
        top: `${getMenuStickyTop()}px`,
        left: 0,
        right: 0,
        width: "100%",
        zIndex: 90,
      };
    }

    if (panelPosition === "after") {
      return {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        zIndex: 60,
      };
    }

    return {
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      width: "100%",
      zIndex: 60,
    };
  };

  // =========================================================
  // COMMON ITEM GRID
  // =========================================================
  //
  // Mobile        = 2
  // Tablet        = 2
  // Desktop       = 2
  // Large desktop = 3
  //
  // =========================================================

  const itemGridClass =
    "grid grid-cols-2 gap-2 min-[420px]:gap-2.5 sm:gap-3 lg:gap-4 xl:grid-cols-3 xl:gap-5";

  const showcaseRowClass = `
  grid
  grid-flow-col

  /* ============================================= */
  /* SAME CARD WIDTH AS NORMAL CATEGORY CARDS */
  /* ============================================= */

  auto-cols-[calc((100%-74px)/2)]

  gap-2

  overflow-x-auto
  overflow-y-hidden

  scroll-smooth

  overscroll-x-contain

  pb-2

  [scrollbar-width:none]
  [-ms-overflow-style:none]
  [&::-webkit-scrollbar]:hidden

  /* 380+ */
  min-[380px]:auto-cols-[calc((100%-84px)/2)]

  /* 420+ */
  min-[420px]:auto-cols-[calc((100%-94px)/2)]
  min-[420px]:gap-2.5

  /* MOBILE / SMALL TABLET */
  sm:auto-cols-[calc((100%-100px)/2)]
  sm:gap-3

  /* TABLET */
  md:auto-cols-[calc((100%-248px)/2)]

  /* LAPTOP */
  lg:auto-cols-[calc((100%-340px)/2)]
  lg:gap-4

  /* LARGE DESKTOP */
  xl:auto-cols-[calc((100%-364px)/3)]
  xl:gap-5
`;

  const scrollShowcaseRow = (sectionId, direction) => {
    const slider = showcaseRowRefs.current[sectionId];

    if (!slider) {
      return;
    }

    const firstCard = slider.firstElementChild;

    if (!firstCard) {
      return;
    }

    // Actual card width
    const cardWidth = firstCard.getBoundingClientRect().width;

    // Actual gap
    const sliderStyles = window.getComputedStyle(slider);

    const gap =
      Number.parseFloat(sliderStyles.columnGap || sliderStyles.gap || "0") || 0;

    // Full card step
    const cardStep = cardWidth + gap;

    // Current screen par kitne COMPLETE cards visible hain
    const visibleCount = Math.max(
      1,
      Math.floor((slider.clientWidth + gap) / cardStep),
    );

    // Ek click me current visible group move hoga
    const scrollAmount = cardStep * visibleCount;

    slider.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,

      behavior: "smooth",
    });
  };

  const renderShowcaseSection = (section) => {
    return (
      <section
        key={section.id}
        className="
        min-w-0
        border-b
        border-[#dfe5eb]
        pb-5
        last:border-b-0
        last:pb-0
        sm:pb-6
      "
      >
        {/* HEADER */}

        <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
          {/* LEFT - TITLE */}

          <div className="min-w-0">
            <h2 className="truncate text-[17px] font-bold leading-tight text-[#172033] sm:text-[20px] md:text-[24px] md:font-semibold lg:text-[26px]">
              {section.title}
            </h2>

            <p className="mt-1 text-[10px] text-[#91a4c1] sm:text-[12px] md:text-[13px]">
              {section.subtitle}
            </p>
          </div>

          {/* RIGHT - LEFT / RIGHT ARROWS */}

          {section.items.length > 3 && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => scrollShowcaseRow(section.id, "left")}
                aria-label={`Previous ${section.title}`}
                className="
          flex
          h-9
          w-9
          items-center
          justify-center

          rounded-full

          border
          border-[#e3e8ef]

          bg-white
          text-[#94a6bd]

          shadow-sm

          transition

          hover:border-[#b9c3d0]
          hover:text-[#111827]

          active:scale-95

          sm:h-10
          sm:w-10

          md:h-11
          md:w-11
        "
              >
                <ArrowLeft size={19} strokeWidth={2} />
              </button>

              <button
                type="button"
                onClick={() => scrollShowcaseRow(section.id, "right")}
                aria-label={`Next ${section.title}`}
                className="
          flex
          h-9
          w-9
          items-center
          justify-center

          rounded-full

          border
          border-[#e3e8ef]

          bg-white
          text-[#94a6bd]

          shadow-sm

          transition

          hover:border-[#b9c3d0]
          hover:text-[#111827]

          active:scale-95

          sm:h-10
          sm:w-10

          md:h-11
          md:w-11
        "
              >
                <ArrowRight size={19} strokeWidth={2} />
              </button>
            </div>
          )}
        </div>

        {/* ONE ROW ONLY */}

        <div
          ref={(element) => {
            if (element) {
              showcaseRowRefs.current[section.id] = element;
            } else {
              delete showcaseRowRefs.current[section.id];
            }
          }}
          className={showcaseRowClass}
        >
          {section.items.map(({ item, category }, index) => (
            <div
              key={`${section.id}-${item?.item_id || index}`}
              className="min-w-0 snap-start"
            >
              <MenuItemCard
                item={item}
                category={category}
                cleanUserId={cleanUserId}
                cleanOrderType={cleanOrderType}
                customerId={customerId}
                isRestaurantClosed={isRestaurantClosed}
                itemLikeUpdatingIds={itemLikeUpdatingIds}
                isItemLiked={isItemLiked}
                toggleFavourite={toggleFavourite}
                getQuantity={getQuantity}
                addItem={addItem}
                removeItem={removeItem}
                handleCustomize={handleCustomize}
                handleCustomizableAdd={handleCustomizableAdd}
                openItemDetails={openItemDetails}
              />
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <>
      <ShimmerStyles />
      <section
        id="restaurant-menu-section"
        ref={menuSectionRef}
        className="relative w-full overflow-visible bg-blue-50 [overflow-anchor:none]"
        style={{
          minHeight: isMenuResultEmpty
            ? "auto"
            : `calc(100dvh - ${getMenuStickyTop()}px + ${menuPinDistance}px)`,
        }}
      >
        {/* =================================================== */}
        {/* MENU PANEL */}
        {/* =================================================== */}

        <div style={getPanelStyle()}>
          <div
            ref={menuPanelRef}
            className="
      mx-auto
      flex
      w-full
      max-w-[1290px]
      flex-col
      overflow-hidden
      bg-blue-50

      md:px-3
      md:py-3

      lg:px-8
      lg:py-6
    "
            style={
              isMenuResultEmpty
                ? {
                    height: "auto",
                    maxHeight: "none",
                  }
                : {
                    height: `calc(100dvh - ${getMenuStickyTop()}px)`,
                    maxHeight: `calc(100dvh - ${getMenuStickyTop()}px)`,
                  }
            }
          >
            {/* ================================================= */}
            {/* MOBILE MENU HEADER */}
            {/* ================================================= */}

            <div className="relative z-[110] flex h-[58px] min-h-[58px] w-full shrink-0 items-center justify-center border-b border-[#dfe2e7] bg-white shadow-[0_3px_10px_rgba(15,23,42,0.08)] sm:h-[62px] sm:min-h-[62px] md:hidden">
              {mobileSearchOpen ? (
                <div className="absolute inset-x-2.5 flex h-[40px] items-center rounded-full border border-[#dce2ea] bg-[#f8fafc] px-3 min-[380px]:inset-x-3 sm:h-[42px]">
                  <Search
                    size={18}
                    strokeWidth={1.8}
                    className="shrink-0 text-[#8795a8]"
                  />

                  <input
                    autoFocus
                    type="text"
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    placeholder="Search menu..."
                    className="h-full min-w-0 flex-1 bg-transparent px-2 text-[12px] text-[#384152] outline-none placeholder:text-[#91a4c1] sm:text-[13px]"
                  />

                  <button
                    type="button"
                    onClick={() => {
                      setMobileSearchOpen(false);
                      setSearchValue("");
                    }}
                    aria-label="Close search"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#343b45] transition hover:bg-[#edf1f5]"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <>
                  <h1 className="text-[19px] font-black uppercase tracking-[-0.7px] text-[#171717] sm:text-[22px]">
                    Menu
                  </h1>

                  <button
                    type="button"
                    onClick={() => setMobileSearchOpen(true)}
                    aria-label="Search menu"
                    className="absolute right-2.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center text-black min-[380px]:right-4 sm:h-10 sm:w-10"
                  >
                    <Search size={22} strokeWidth={1.8} />
                  </button>
                </>
              )}
            </div>

            {/* ================================================= */}
            {/* LOADING - MOVING SHIMMER */}
            {/* ================================================= */}

            {catalogueLoading && (
              <div className="flex min-h-0 flex-1 gap-1.5 overflow-hidden px-1.5 py-3 min-[380px]:gap-2 min-[380px]:px-2 sm:gap-4 sm:px-4 md:gap-5 md:px-0 lg:gap-6">
                {/* =============================================== */}
                {/* LEFT CATEGORY SHIMMER */}
                {/* =============================================== */}

                <aside className="w-[54px] shrink-0 min-[380px]:w-[60px] sm:w-[68px] md:w-[220px] lg:w-[300px]">
                  <div className="bg-white md:rounded-[16px] md:border md:border-[#dce2e8] md:p-4 lg:p-5">
                    {Array.from({ length: 7 }).map((_, index) => (
                      <div
                        key={`category-shimmer-${index}`}
                        className="
              menu-card-shimmer
              mx-auto
              mb-3

              h-[34px]
              w-[34px]
              rounded-full

              min-[380px]:h-[38px]
              min-[380px]:w-[38px]

              sm:h-[42px]
              sm:w-[42px]

              md:h-[46px]
              md:w-full
              md:rounded-full
            "
                      />
                    ))}
                  </div>
                </aside>

                {/* =============================================== */}
                {/* RIGHT ITEM CARD SHIMMER */}
                {/* =============================================== */}

                <main className="min-w-0 flex-1 overflow-hidden">
                  <div className={itemGridClass}>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <div
                        key={`item-card-shimmer-${index}`}
                        className="
              min-w-0
              overflow-hidden
              rounded-[12px]
              border
              border-[#dce2e8]
              bg-white
              sm:rounded-[14px]
            "
                      >
                        {/* IMAGE */}
                        <div className="menu-card-shimmer aspect-[4/3] w-full" />

                        {/* CONTENT */}
                        <div className="flex min-h-[130px] flex-col p-2.5 sm:min-h-[150px] sm:p-3 lg:p-4">
                          {/* NAME */}
                          <div className="menu-card-shimmer h-4 w-[78%] rounded-full sm:h-5" />

                          {/* DESCRIPTION */}
                          <div className="mt-2 space-y-1.5">
                            <div className="menu-card-shimmer h-3 w-full rounded-full" />

                            <div className="menu-card-shimmer h-3 w-[82%] rounded-full" />
                          </div>

                          {/* SIZE */}
                          <div className="menu-card-shimmer mt-3 h-5 w-[58px] rounded-full sm:h-6 sm:w-[66px]" />

                          {/* PRICE + ADD */}
                          <div className="mt-auto flex items-center justify-between gap-2 pt-3">
                            <div className="menu-card-shimmer h-5 w-[52px] rounded-full sm:h-6 sm:w-[65px]" />

                            <div className="menu-card-shimmer h-8 w-[58px] rounded-full min-[380px]:w-[64px] sm:h-10 sm:w-[78px]" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </main>
              </div>
            )}

            {/* ================================================= */}
            {/* ERROR */}
            {/* ================================================= */}

            {!catalogueLoading && catalogueError && (
              <div className="mx-3 my-5 flex min-h-0 flex-1 flex-col items-center justify-center rounded-[18px] border border-dashed border-[#d7dde5] bg-white px-5 text-center sm:mx-5 md:mx-0">
                <p className="text-[15px] font-semibold text-[#303640] sm:text-[16px]">
                  Catalogue could not be loaded
                </p>

                <p className="mt-2 max-w-md text-[12px] text-[#8491a4] sm:text-[14px]">
                  {catalogueError}
                </p>

                <button
                  type="button"
                  onClick={handleRetry}
                  className="mt-5 flex h-10 items-center gap-2 rounded-xl bg-[#11182a] px-4 text-xs font-semibold text-white sm:h-11 sm:px-5 sm:text-sm"
                >
                  <RefreshCw size={17} />
                  Try again
                </button>
              </div>
            )}

            {/* ================================================= */}
            {/* MENU CONTENT */}
            {/* ================================================= */}
            {!catalogueLoading &&
              !catalogueError &&
              Array.isArray(catalogueMenu) &&
              catalogueMenu.length > 0 && (
                <main
                  ref={menuScrollRef}
                  onScroll={handleMenuScroll}
                  className="
        min-h-0
        min-w-0
        flex-1

        overflow-x-hidden
        overflow-y-hidden

        touch-pan-y

        bg-blue-50

        [scrollbar-width:none]
        [-ms-overflow-style:none]
        [&::-webkit-scrollbar]:hidden
      "
                >
                  <div className="min-w-0 pb-4">
                    {/* ================================================= */}
                    {/* RESTAURANT CLOSED ALERT */}
                    {/* ================================================= */}

                    {isRestaurantClosed && (
                      <div className="px-3 pt-3 sm:px-4 md:px-0 md:pt-4">
                        <div
                          className="
        relative
        overflow-hidden
        rounded-[16px]
        border
        border-[#f1d7d5]
        bg-white
        shadow-[0_6px_20px_rgba(15,23,42,0.05)]

        sm:rounded-[18px]
      "
                        >
                          {/* SOFT BACKGROUND */}
                          <div
                            className="
          pointer-events-none
          absolute
          right-[-35px]
          top-[-45px]
          h-[120px]
          w-[120px]
          rounded-full
          bg-[#fff0ee]
        "
                          />

                          <div
                            className="
          relative
          flex
          items-start
          gap-3

          px-3.5
          py-3.5

          sm:items-center
          sm:gap-4
          sm:px-5
          sm:py-4
        "
                          >
                            {/* ICON */}
                            <div
                              className="
            flex
            h-10
            w-10
            shrink-0
            items-center
            justify-center
            rounded-full
            bg-[#fff0ee]
            text-[#e5484d]

            sm:h-11
            sm:w-11
          "
                            >
                              <Clock3 size={19} strokeWidth={2} />
                            </div>

                            {/* CONTENT */}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3
                                  className="
                text-[13px]
                font-bold
                text-[#202938]

                sm:text-[15px]
              "
                                >
                                  Restaurant is currently closed
                                </h3>

                                {/* STATUS BADGE */}
                                {/* <span
                                  className="
                inline-flex
                items-center
                gap-1.5
                rounded-full
                bg-[#fff0ee]
                px-2.5
                py-1
                text-[9px]
                font-bold
                uppercase
                tracking-[0.04em]
                text-[#d92d20]

                sm:text-[10px]
              "
                                >
                                  <span className="h-1.5 w-1.5 rounded-full bg-[#d92d20]" />
                                  Currently closed
                                </span> */}
                              </div>

                              <p
                                className="
              mt-1
              max-w-[650px]
              text-[10px]
              leading-4
              text-[#7c8797]

              sm:mt-1.5
              sm:text-[12px]
              sm:leading-5
            "
                              >
                                You can still explore the menu and decide what
                                you'd like. Ordering will be available once the
                                restaurant opens again.
                              </p>
                            </div>
                          </div>

                          {/* BOTTOM INFO STRIP */}
                          <div
                            className="
          flex
          items-center
          gap-2
          border-t
          border-[#f4e5e3]
          bg-[#fffaf9]
          px-3.5
          py-2.5
          text-[9px]
          font-medium
          text-[#8b5e58]

          sm:px-5
          sm:text-[11px]
        "
                          >
                            <Clock3 size={13} strokeWidth={2} />
                            Menu browsing is available • Ordering is temporarily
                            paused
                          </div>
                        </div>
                      </div>
                    )}
                    {/* ================================================= */}
                    {/* TOP SECTIONS */}
                    {/* LEFT CATEGORY YAHAN NAHI HOGI */}
                    {/* ================================================= */}

                    {showShowcaseSections && (
                      <div
                        className="
              space-y-5
              px-3
              pb-6
              pt-4

              min-[380px]:px-3.5

              sm:space-y-6
              sm:px-4
              sm:pb-7

              md:px-0
              md:pb-8
              md:pt-5
            "
                      >
                        {showcaseSections.map(renderShowcaseSection)}
                      </div>
                    )}

                    {/* ================================================= */}
                    {/* SEPARATOR */}
                    {/* ================================================= */}

                    {showShowcaseSections && (
                      <div className="mx-3 mb-5 border-t border-[#d9e0e8] sm:mx-4 md:mx-0 md:mb-6" />
                    )}

                    {/* ================================================= */}
                    {/* MENU HEADER - SHOWCASE KE BAAD */}
                    {/* ================================================= */}

                    <div
                      ref={menuStickyHeaderRef}
                      data-menu-sticky-header="true"
                      className="sticky top-0 z-[100] w-full bg-blue-50"
                    >
                      {/* ================================================= */}
                      {/* DESKTOP MENU HEADER */}
                      {/* ================================================= */}

                      <div
                        className="
    hidden
    w-full
    shrink-0
    items-start
    justify-between
    gap-4

    border-b
    border-[#e3e8ee]

    bg-blue-50

    px-1
    py-4

    md:flex
    md:pt-7

    lg:gap-6
    lg:py-5
    lg:pt-8

    xl:pt-5
  "
                      >
                        <div className="min-w-0">
                          <h1 className="text-[28px] font-medium leading-tight text-black lg:text-[36px]">
                            Menu
                          </h1>

                          <p className="mt-1 max-w-[500px] truncate text-[14px] text-[#91a4c1] lg:max-w-[600px] lg:text-[16px]">
                            {restaurantDetails?.restaurant_name
                              ? `${restaurantDetails.restaurant_name} • ${
                                  restaurantDetails.city || "Restaurant menu"
                                }`
                              : "Restaurant menu"}
                          </p>
                        </div>

                        <div className="flex h-[44px] w-full max-w-[300px] items-center rounded-full border border-[#dce2ea] bg-white px-4 lg:h-[46px] lg:max-w-[425px]">
                          <Search
                            size={20}
                            strokeWidth={1.8}
                            className="shrink-0 text-[#8ea1bd]"
                          />

                          <input
                            type="text"
                            value={searchValue}
                            onChange={(event) =>
                              setSearchValue(event.target.value)
                            }
                            placeholder="Search dishes, categories..."
                            className="h-full min-w-0 flex-1 bg-transparent px-3 text-[13px] text-[#384152] outline-none placeholder:text-[#91a4c1]"
                          />
                        </div>
                      </div>

                      {/* ================================================= */}
                      {/* SORT BY + FILTERS */}
                      {/* MENU LINE KE JUST NICHE */}
                      {/* ================================================= */}

                      <div
                        className="
    w-full
    border-b
    border-[#e3e8ee]
    bg-blue-50

    px-1
    py-3

    md:py-4
  "
                      >
                        <div
                          className="
      flex
      min-w-0
      items-center
      gap-2.5

      sm:gap-3
    "
                        >
                          {/* ============================================= */}
                          {/* FILTERS */}
                          {/* ============================================= */}

                          <div
                            onWheel={(event) => {
                              const element = event.currentTarget;

                              const canScroll =
                                element.scrollWidth > element.clientWidth;

                              if (!canScroll) {
                                return;
                              }

                              const scrollingRight = event.deltaY > 0;

                              const scrollingLeft = event.deltaY < 0;

                              const isAtStart = element.scrollLeft <= 0;

                              const isAtEnd =
                                Math.ceil(
                                  element.scrollLeft + element.clientWidth,
                                ) >= element.scrollWidth;

                              if (
                                (scrollingLeft && isAtStart) ||
                                (scrollingRight && isAtEnd)
                              ) {
                                return;
                              }

                              event.preventDefault();

                              element.scrollBy({
                                left: event.deltaY,
                                behavior: "auto",
                              });
                            }}
                            className="
        flex
        min-w-0
        flex-1

        gap-2

        overflow-x-auto
        overflow-y-hidden

        overscroll-x-contain
        touch-pan-x

        [scrollbar-width:none]
        [-ms-overflow-style:none]
        [&::-webkit-scrollbar]:hidden

        sm:gap-3
      "
                          >
                            {menuFilters.map((filter) => {
                              const isActive = activeFilter === filter.id;

                              return (
                                <button
                                  key={filter.id}
                                  type="button"
                                  disabled={catalogueLoading}
                                  onClick={() => setActiveFilter(filter.id)}
                                  className={`
              shrink-0
              whitespace-nowrap

              rounded-full
              border

              font-medium

              transition

              h-[36px]
              px-3
              text-[11px]

              min-[380px]:h-[38px]
              min-[380px]:px-4
              min-[380px]:text-[12px]

              sm:h-[40px]
              sm:px-5
              sm:text-[13px]

              md:h-[42px]
              md:text-[14px]

              lg:h-[46px]
              lg:px-5
              lg:text-[15px]

              disabled:cursor-not-allowed
              disabled:opacity-60

              ${
                isActive
                  ? `
                    border-[#11182a]
                    bg-[#11182a]
                    text-white
                  `
                  : `
                    border-[#dce2e8]
                    bg-white
                    text-[#555]

                    hover:border-[#aeb8c7]
                  `
              }
            `}
                                >
                                  {filter.label}
                                </button>
                              );
                            })}
                          </div>

                          {/* ============================================= */}
                          {/* SORT BY */}
                          {/* ============================================= */}

                          <div
                            ref={sortDropdownRef}
                            className="
    relative
    z-[80]
    w-[112px]
    shrink-0

    sm:w-[160px]
  "
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setSortOpen((previous) => !previous)
                              }
                              className="
          flex
          h-[36px]
          w-full
          cursor-pointer
          items-center
          justify-between

          rounded-[9px]

          border
          border-[#dfe4ea]

          bg-white

          px-3

          text-left
          text-[11px]
          text-[#555]

          outline-none
          transition

          hover:border-[#c4ccd6]
          focus:border-[#9eabc0]

          min-[380px]:h-[38px]
          min-[380px]:text-[12px]

          sm:h-[40px]
          sm:px-4
          sm:text-[12px]

          md:h-[42px]

          lg:h-[46px]
        "
                            >
                              <span className="truncate">
                                {selectedSortLabel}
                              </span>

                              <ChevronDown
                                size={16}
                                strokeWidth={2}
                                className={`
            ml-2
            shrink-0
            text-[#91a2ba]
            transition-transform
            duration-200

            ${sortOpen ? "rotate-180" : ""}
          `}
                              />
                            </button>

                            {/* DROPDOWN */}

                            {sortOpen && (
                              <div
                                className="
            absolute
            left-0
            top-full
            z-[200]

            mt-[2px]

            w-full

            overflow-hidden

            rounded-[10px]

            border
            border-[#dfe4ea]

            bg-white
            py-1

            shadow-[0_8px_24px_rgba(15,23,42,0.12)]
          "
                              >
                                {sortOptions.map((option) => {
                                  const isSelected = sortBy === option.value;

                                  return (
                                    <button
                                      key={option.value}
                                      type="button"
                                      onClick={() => {
                                        setSortBy(option.value);

                                        setSortOpen(false);
                                      }}
                                      className={`
                  block
                  w-full

                  px-3
                  py-2

                  text-left
                  text-[11px]

                  transition

                  sm:px-4
                  sm:text-[13px]

                  ${
                    isSelected
                      ? "bg-[#2B4578] text-white"
                      : "bg-white text-[#555] hover:bg-[#f5f7fa]"
                  }
                `}
                                    >
                                      {option.label}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* END STICKY MENU HEADER + FILTERS */}
                    </div>

                    {filteredCategories.length > 0 ? (
                      <>
                        {/* AB LEFT CATEGORY DIKHEGI */}
                        {/* ================================================= */}

                        <div
                          className="
  relative
  flex
  min-w-0
  items-start

  pt-4

  md:gap-4
  lg:gap-6
"
                        >
                          {/* =============================================== */}
                          {/* LEFT CATEGORY */}
                          {/* =============================================== */}

                          <aside
                            ref={leftCategoryScrollRef}
                            className="
    sticky
    z-[80]

    w-[64px]
    shrink-0

    overflow-y-auto
    overflow-x-hidden

    scroll-smooth

    border-r
    border-[#e2e5e9]

    bg-[#fafafa]

    py-0.5

    [scrollbar-width:none]
    [&::-webkit-scrollbar]:hidden

    min-[380px]:w-[70px]

    sm:w-[68px]

    md:w-[220px]
    md:border-r-0
    md:bg-transparent

    lg:w-[300px]

  "
                            style={{
                              top: `${menuStickyHeaderHeight}px`,
                              maxHeight: `calc(100dvh - ${
                                getMenuStickyTop() + menuStickyHeaderHeight + 12
                              }px)`,
                            }}
                          >
                            <div className="bg-white md:rounded-[16px] md:border md:border-[#dce2e8] md:p-4 lg:p-5">
                              <nav className="flex flex-col md:gap-1">
                                {filteredCategories.map((category) => {
                                  const categoryId = String(
                                    category?.cat_id || "",
                                  );

                                  const isActive =
                                    activeCategory === categoryId;

                                  return (
                                    <button
                                      ref={(element) => {
                                        if (element) {
                                          categoryButtonRefs.current[
                                            categoryId
                                          ] = element;
                                        } else {
                                          delete categoryButtonRefs.current[
                                            categoryId
                                          ];
                                        }
                                      }}
                                      key={categoryId}
                                      type="button"
                                      onClick={() =>
                                        handleCategoryClick(categoryId)
                                      }
                                      className={`
                        flex
                        w-full
                        min-w-0
                        flex-col
                        items-center
                        justify-center
                        gap-1

                        px-0.5
                        py-2.5

                        text-center
                        transition

                        min-[380px]:px-1

                        sm:px-1
                        sm:py-3

                        md:min-h-[46px]
                        md:flex-row
                        md:justify-start
                        md:gap-2
                        md:rounded-full
                        md:px-4
                        md:py-2
                        md:text-left

                        lg:min-h-[48px]
                        lg:px-5

                        ${
                          isActive
                            ? "bg-[#EAF2FF] text-[#2563EB] md:bg-[#1E3A8A] md:text-white"
                            : "text-[#222] md:text-[#555]"
                        }
                      `}
                                    >
                                      {/* CATEGORY IMAGE */}

                                      <div
                                        className={`
                          h-[34px]
                          w-[34px]
                          shrink-0
                          overflow-hidden
                          rounded-full
                          border
                          bg-white

                          min-[380px]:h-[38px]
                          min-[380px]:w-[38px]

                          sm:h-[42px]
                          sm:w-[42px]

                          md:hidden

                          ${isActive ? "border-blue-600" : "border-[#dfe3e8]"}
                        `}
                                      >
                                        <img
                                          src={
                                            category?.cat_img_url || ChaafoLogo
                                          }
                                          alt={
                                            category?.category_name ||
                                            "Category"
                                          }
                                          loading="lazy"
                                          className="h-full w-full object-cover"
                                          onError={(event) => {
                                            event.currentTarget.onerror = null;

                                            event.currentTarget.src =
                                              ChaafoLogo;

                                            event.currentTarget.className =
                                              "h-full w-full bg-white object-contain p-1";
                                          }}
                                        />
                                      </div>

                                      {/* CATEGORY NAME */}

                                      <span
                                        className="
                          w-full
                          min-w-0

                          px-0.5

                          text-center
                          text-[9px]
                          font-medium
                          leading-[11px]

                          min-[380px]:text-[10px]
                          min-[380px]:leading-[12px]

                          sm:text-[11px]
                          sm:leading-[14px]

                          md:px-0
                          md:text-left
                          md:text-[13px]
                          md:leading-[17px]

                          lg:text-[15px]
                          lg:leading-[19px]

                          xl:text-[16px]
                        "
                                      >
                                        {(() => {
                                          const categoryName = String(
                                            category?.category_name ||
                                              "Category",
                                          ).trim();

                                          const bracketIndex =
                                            categoryName.indexOf("(");

                                          if (bracketIndex === -1) {
                                            return categoryName;
                                          }

                                          const firstLine = categoryName
                                            .slice(0, bracketIndex)
                                            .trim();

                                          const secondLine = categoryName
                                            .slice(bracketIndex)
                                            .trim()
                                            .replace(/^\(\s*/, "(");

                                          return (
                                            <>
                                              <span className="block whitespace-normal break-words">
                                                {firstLine}
                                              </span>

                                              <span className="mt-0.5 block">
                                                {secondLine}
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </span>
                                    </button>
                                  );
                                })}
                              </nav>
                            </div>
                          </aside>

                          {/* =============================================== */}
                          {/* RIGHT NORMAL MENU ITEMS */}
                          {/* =============================================== */}

                          <div
                            className="
              min-w-0
              flex-1

              bg-blue-50

              pl-1
              pr-1.5
              pb-3

              min-[380px]:pl-1.5
              min-[380px]:pr-2

              sm:pl-2
              sm:pr-3

              md:bg-transparent
              md:px-0
            "
                          >
                            <div className="space-y-6 sm:space-y-7 md:space-y-9">
                              {filteredCategories.map((category) => {
                                const categoryId = String(
                                  category?.cat_id || "",
                                );

                                const menuItems = Array.isArray(
                                  category?.menu_items,
                                )
                                  ? category.menu_items
                                  : [];

                                return (
                                  <section
                                    key={categoryId}
                                    ref={(element) => {
                                      sectionRefs.current[categoryId] = element;
                                    }}
                                    className="scroll-mt-2"
                                  >
                                    {/* CATEGORY TITLE */}

                                    <div className="mb-2 sm:mb-3">
                                      <h2 className="break-words text-[14px] font-bold leading-tight text-[#172033] min-[380px]:text-[17px] sm:text-[19px] md:text-[24px] md:font-medium md:text-black lg:text-[26px]">
                                        {category?.category_name}
                                      </h2>

                                      <p className="mt-1 hidden text-[14px] text-[#91a4c1] md:block md:text-[15px] lg:text-[16px]">
                                        {menuItems.length}{" "}
                                        {menuItems.length === 1
                                          ? "item"
                                          : "items"}
                                        {category?.category_description && (
                                          <>
                                            <span className="mx-1">•</span>

                                            {category.category_description}
                                          </>
                                        )}
                                      </p>
                                    </div>

                                    {/* NORMAL GRID */}

                                    <div className={itemGridClass}>
                                      {menuItems.map((item) => (
                                        <MenuItemCard
                                          key={String(item?.item_id || "")}
                                          item={item}
                                          category={category}
                                          isRestaurantClosed={
                                            isRestaurantClosed
                                          }
                                          cleanUserId={cleanUserId}
                                          cleanOrderType={cleanOrderType}
                                          customerId={customerId}
                                          itemLikeUpdatingIds={
                                            itemLikeUpdatingIds
                                          }
                                          isItemLiked={isItemLiked}
                                          toggleFavourite={toggleFavourite}
                                          getQuantity={getQuantity}
                                          addItem={addItem}
                                          removeItem={removeItem}
                                          handleCustomize={handleCustomize}
                                          handleCustomizableAdd={
                                            handleCustomizableAdd
                                          }
                                          openItemDetails={openItemDetails}
                                        />
                                      ))}
                                    </div>
                                  </section>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div
                        className="
    flex
    h-[250px]
    w-full
    flex-col
    items-center
    justify-center

    px-4

    text-center

    sm:h-[270px]
    md:h-[280px]
  "
                      >
                        <div
                          className="
        flex
        max-w-[420px]
        flex-col
        items-center
        justify-center

        rounded-[18px]
        border
        border-dashed
        border-[#d7dde5]

        bg-white

        px-6
        py-8

        sm:px-8
        sm:py-10
      "
                        >
                          <p className="text-[14px] font-semibold text-[#303640] sm:text-[16px]">
                            No items available
                          </p>

                          <p className="mt-1.5 text-[11px] leading-5 text-[#8491a4] sm:text-[13px]">
                            No catalogue items match the selected filter.
                          </p>

                          {activeFilter !== "all" && (
                            <button
                              type="button"
                              onClick={() => {
                                resetMenuToFirstRef.current = true;

                                setActiveFilter("all");
                              }}
                              className="
            mt-4
            rounded-full
            bg-[#11182a]
            px-4
            py-2

            text-[11px]
            font-semibold
            text-white

            transition
            hover:bg-[#202938]

            sm:text-[12px]
          "
                            >
                              View all items
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </main>
              )}

            {/* ================================================= */}
            {/* EMPTY */}
            {/* ================================================= */}

            {!catalogueLoading &&
              !catalogueError &&
              (!Array.isArray(catalogueMenu) || catalogueMenu.length === 0) && (
                <div className="mx-3 my-5 flex min-h-0 flex-1 items-center justify-center rounded-[18px] border border-dashed border-[#d7dde5] bg-white px-4 text-center sm:mx-5 sm:px-5 md:mx-0">
                  <p className="text-[12px] text-[#8491a4] sm:text-[15px]">
                    No catalogue items available.
                  </p>
                </div>
              )}
          </div>
        </div>

        {/* =================================================== */}
        {/* FLOATING CART */}
        {/* =================================================== */}

        {!isRestaurantClosed && itemCount > 0 && (
          <button
            id="restaurant-view-cart-button"
            type="button"
            onClick={() => setCartOpen(true)}
            className={`
    ${cartDockTop !== null ? "absolute" : "fixed"}

    z-[900]

   bottom-4
right-[80px]

sm:bottom-3
sm:right-20

    md:bottom-4
    md:right-20

    lg:bottom-4
    lg:right-20

    xl:bottom-4
    xl:right-20

    flex
    h-[48px]
    w-fit
    max-w-[calc(100vw-16px)]
    items-center

    rounded-full
    bg-blue-700

    pl-2
    pr-3.5

    text-white

    shadow-[0_16px_35px_rgba(29,78,216,0.35)]

    transition-colors
    duration-200

    hover:bg-blue-800
    active:scale-[0.99]

    min-[380px]:h-[50px]
    min-[380px]:pl-2.5
    min-[380px]:pr-4

    sm:h-[54px]
    sm:pl-2.5
    sm:pr-4.5
  `}
            style={
              cartDockTop !== null
                ? {
                    top: `${cartDockTop}px`,
                    bottom: "auto",
                  }
                : undefined
            }
          >
            <div className="flex min-w-0 items-center gap-2">
              {/* ================================================= */}
              {/* LEFT - CART ITEM IMAGES */}
              {/* 3 IMAGES + 1 REMAINING COUNT CIRCLE */}
              {/* ================================================= */}

              <div className="flex shrink-0 items-center">
                {/* ONLY FIRST 2 ITEM IMAGES */}
                {items.slice(0, 2).map((item, index) => (
                  <div
                    key={`${item.itemId || item.item_id || index}-${index}`}
                    className={`
        relative

        h-9
        w-9

        shrink-0
        overflow-hidden

        rounded-full

        border-2
        border-blue-700

        bg-white
        shadow-md

        ${index > 0 ? "-ml-4 sm:-ml-5" : ""}

        min-[380px]:h-10
        min-[380px]:w-10

        sm:h-11
        sm:w-11
      `}
                    style={{
                      zIndex: 40 - index,
                    }}
                  >
                    <img
                      src={
                        item.image || item.itemImage || item.photo || ChaafoLogo
                      }
                      alt={
                        item.name ||
                        item.itemName ||
                        item.item_name ||
                        "Cart item"
                      }
                      className="h-full w-full object-cover"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = ChaafoLogo;
                        event.currentTarget.className =
                          "h-full w-full bg-white object-contain p-1";
                      }}
                    />
                  </div>
                ))}

                {/* AFTER 2 ITEMS SHOW REMAINING COUNT */}
                {items.length > 2 && (
                  <div
                    className="
        relative
        z-[37]

        -ml-3
        sm:-ml-4

        flex
        h-9
        w-9

        shrink-0
        items-center
        justify-center

        rounded-full

        border-2
        border-blue-700

        bg-white

        text-[9px]
        font-bold
        text-blue-700

        shadow-md

        min-[380px]:h-10
        min-[380px]:w-10
        min-[380px]:text-[10px]

        sm:h-11
        sm:w-11
        sm:text-[11px]
      "
                  >
                    +{items.length - 2}
                  </div>
                )}
              </div>

              {/* ================================================= */}
              {/* RIGHT - VIEW CART TEXT */}
              {/* ================================================= */}

              <div
                className="
          flex
          shrink-0
          flex-col
          items-start
          justify-center

          pr-0.5
          text-left
        "
              >
                <span
                  className="
            whitespace-nowrap

            text-[12px]
            font-bold
            leading-tight

            min-[380px]:text-[13px]

            sm:text-[15px]
          "
                >
                  View Cart
                </span>

                <span
                  className="
            mt-0.5
            whitespace-nowrap

            text-[9px]
            leading-tight
            text-blue-100

            min-[380px]:text-[10px]

            sm:text-xs
          "
                >
                  {itemCount} {itemCount === 1 ? "item" : "items"}
                </span>
              </div>
            </div>
          </button>
        )}

        {/* =================================================== */}
        {/* ITEM DETAILS MODAL - PORTAL */}
        {/* =================================================== */}

        {selectedItemDetail &&
          typeof document !== "undefined" &&
          createPortal(
            (() => {
              const detailPriceDetails = getPriceDetails(selectedItemDetail);

              const detailPriceOptions = detailPriceDetails.priceOptions || [];

              const selectedDetailPrice =
                detailPriceOptions[selectedDetailPriceIndex] ||
                detailPriceOptions[0] ||
                null;

              const selectedDetailVariantItem = buildSelectedVariantItem(
                selectedItemDetail,
                selectedDetailPrice,
              );

              const detailHasCustomizations =
                Array.isArray(selectedDetailPrice?.customizations) &&
                selectedDetailPrice.customizations.length > 0;

              const isAvailable = getItemAvailability(
                selectedItemDetail,
                cleanOrderType,
              );

              const detailItemId = String(
                selectedItemDetail?.item_id || selectedItemDetail?.itemId || "",
              ).trim();

              const detailQuantity = getQuantity(detailItemId);

              return (
                <div
                  className="
            fixed
            inset-0
            z-[99999]
            flex
            items-center
            justify-center
            bg-black/60
            p-3
            backdrop-blur-[1px]

            sm:p-5
          "
                  onClick={() => {
                    setSelectedItemDetail(null);
                    setSelectedDetailPriceIndex(0);
                  }}
                >
                  <div
                    className="
              flex
              max-h-[90dvh]
              w-full
              max-w-[460px]
              flex-col
              overflow-hidden
              rounded-[18px]
              bg-white
              shadow-2xl

              sm:max-w-[520px]
              sm:rounded-[22px]
            "
                    onClick={(event) => event.stopPropagation()}
                  >
                    {/* ======================================= */}
                    {/* MODAL IMAGE */}
                    {/* ======================================= */}

                    <div
                      className="
                relative
                aspect-[4/3]
                w-full
                shrink-0
                overflow-hidden
                bg-[#eef1f4]
              "
                    >
                      <img
                        src={
                          selectedItemDetail?.photo ||
                          selectedItemDetail?.category?.cat_img_url ||
                          ChaafoLogo
                        }
                        alt={selectedItemDetail?.item_name || "Menu item"}
                        className="
                  block
                  h-full
                  w-full
                  object-fill
                "
                        onError={(event) => {
                          event.currentTarget.onerror = null;

                          event.currentTarget.src = ChaafoLogo;

                          event.currentTarget.className =
                            "block h-full w-full bg-white object-contain p-6 sm:p-8";
                        }}
                      />

                      {/* CLOSE */}

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedItemDetail(null);
                          setSelectedDetailPriceIndex(0);
                        }}
                        className="
                  absolute
                  right-2.5
                  top-2.5
                  z-20
                  flex
                  h-9
                  w-9
                  items-center
                  justify-center
                  rounded-full
                  bg-white/95
                  text-[#344054]
                  shadow-md
                  transition

                  hover:bg-white

                  sm:right-3
                  sm:top-3
                  sm:h-10
                  sm:w-10
                "
                      >
                        <X size={19} />
                      </button>

                      {/* OUT OF STOCK */}

                      {!isAvailable && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/35">
                          <span className="rounded-full bg-white px-4 py-2 text-xs font-semibold sm:text-sm">
                            Out of stock
                          </span>
                        </div>
                      )}
                    </div>

                    {/* ======================================= */}
                    {/* MODAL CONTENT */}
                    {/* ======================================= */}

                    <div
                      className="
                min-h-0
                flex-1
                overflow-y-auto
                p-4
                pb-[max(10px,env(safe-area-inset-bottom))]
              "
                    >
                      {/* ITEM NAME */}

                      <h2 className="line-clamp-2 text-[17px] font-bold leading-6 text-[#273142] sm:text-[20px]">
                        {selectedItemDetail?.item_name || "Menu item"}
                      </h2>

                      {/* DESCRIPTION */}

                      <p className="mt-2 text-[12px] font-normal leading-4 text-[#707a8a] sm:text-[13px] sm:leading-5">
                        {selectedItemDetail?.item_description ||
                          "Freshly prepared."}
                      </p>

                      {/* =================================== */}
                      {/* SIZE OPTIONS */}
                      {/* =================================== */}

                      {detailPriceOptions.length > 0 && (
                        <div className="mt-4">
                          <p className="mb-2 text-[11px] font-semibold text-gray-500 sm:text-[12px]">
                            Select size
                          </p>

                          <div className="flex flex-wrap items-center gap-2">
                            {detailPriceOptions.map((priceOption, index) => {
                              const isSelected =
                                selectedDetailPriceIndex === index;

                              return (
                                <button
                                  key={`detail-size-${priceOption.index}`}
                                  type="button"
                                  onClick={() =>
                                    setSelectedDetailPriceIndex(index)
                                  }
                                  className={`
                              inline-flex
                              items-center
                              justify-center
                              rounded-full
                              border

                              px-3
                              py-1.5

                              text-[11px]
                              font-semibold

                              transition

                              sm:px-3.5
                              sm:py-2
                              sm:text-[12px]

                              ${
                                isSelected
                                  ? `
                                    border-[#2454e6]
                                    bg-[#2454e6]
                                    text-white
                                  `
                                  : `
                                    border-[#dce2e8]
                                    bg-white
                                    text-gray-600

                                    hover:border-[#2454e6]
                                    hover:text-[#2454e6]
                                  `
                              }
                            `}
                                >
                                  {priceOption.size}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* =================================== */}
                      {/* CUSTOMIZABLE */}
                      {/* =================================== */}

                      {detailHasCustomizations && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => {
                              const currentCategory =
                                selectedItemDetail?.category || {};

                              setSelectedItemDetail(null);

                              setSelectedDetailPriceIndex(0);

                              handleCustomizableAdd(
                                selectedDetailVariantItem,
                                currentCategory,
                                detailQuantity,
                              );
                            }}
                            className="
                      inline-flex
                      cursor-pointer
                      rounded-full
                      border
                      border-blue-200
                      bg-blue-50

                      px-2.5
                      py-1

                      text-[10px]
                      font-semibold
                      text-blue-700

                      transition

                      hover:border-blue-300
                      hover:bg-blue-100

                      sm:text-[11px]
                    "
                          >
                            Customizable
                          </button>
                        </div>
                      )}

                      {/* =================================== */}
                      {/* PRICE + ADD */}
                      {/* =================================== */}

                      <div
                        className="
                  mt-2
                  flex
                  items-end
                  justify-between
                  gap-3
                  border-t
                  border-[#edf0f3]
                  pt-2

                  sm:mt-5
                "
                      >
                        {/* SELECTED PRICE */}

                        <div className="min-w-0">
                          {selectedDetailPrice ? (
                            <>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-[19px] font-bold text-[#172033] sm:text-[22px]">
                                  {selectedDetailPrice.formattedSellingPrice}
                                </span>

                                {selectedDetailPrice.hasDiscount && (
                                  <span className="text-[11px] text-[#91a4c1] line-through sm:text-[13px]">
                                    {selectedDetailPrice.formattedOriginalPrice}
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="text-sm text-gray-400">
                              Price unavailable
                            </span>
                          )}
                        </div>

                        {/* ADD */}

                        {isAvailable && selectedDetailPrice && (
                          <button
                            type="button"
                            disabled={isRestaurantClosed}
                            onClick={() => {
                              const currentCategory =
                                selectedItemDetail?.category || {};

                              if (isRestaurantClosed) {
                                return;
                              }

                              if (detailHasCustomizations) {
                                setSelectedItemDetail(null);
                                setSelectedDetailPriceIndex(0);

                                handleCustomizableAdd(
                                  selectedDetailVariantItem,
                                  currentCategory,
                                  detailQuantity,
                                );

                                return;
                              }

                              setSelectedItemDetail(null);
                              setSelectedDetailPriceIndex(0);

                              addItem(
                                selectedDetailVariantItem,
                                currentCategory,
                              );
                            }}
                            className="
                        flex
                        h-10
                        min-w-[84px]
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-[#2454e6]

                        px-5

                        text-[12px]
                        font-bold
                        text-white

                        transition

                        hover:bg-[#1d4ed8]
                        active:scale-95
                        disabled:cursor-not-allowed
disabled:bg-gray-300
disabled:text-gray-500
disabled:hover:bg-gray-300
disabled:active:scale-100

                        sm:h-11
                        sm:min-w-[96px]
                        sm:text-[14px]
                      "
                          >
                            ADD
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })(),
            document.body,
          )}

        {/* =================================================== */}
        {/* CUSTOMIZATION MODAL - PORTAL */}
        {/* =================================================== */}

        {selectedCustomizationItem &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 p-3 backdrop-blur-[1px] sm:p-5"
              onClick={() => {
                setSelectedCustomizationItem(null);

                setCustomizationValues({});
              }}
            >
              <div
                className="flex max-h-[88dvh] w-full max-w-[460px] flex-col overflow-hidden rounded-[18px] bg-white shadow-2xl sm:max-w-[500px] sm:rounded-[22px]"
                onClick={(event) => event.stopPropagation()}
              >
                {/* =========================================== */}
                {/* HEADER */}
                {/* =========================================== */}

                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3.5 sm:items-center sm:px-5 sm:py-4">
                  <div className="min-w-0 flex-1">
                    <h2 className="line-clamp-2 break-words text-[15px] font-bold leading-5 text-gray-900 sm:text-[18px] sm:leading-6">
                      Customize {selectedCustomizationItem?.item_name}
                    </h2>

                    <p className="mt-1 text-[10px] text-gray-500 sm:text-[12px]">
                      Select your preferences
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCustomizationItem(null);

                      setCustomizationValues({});
                    }}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* =========================================== */}
                {/* CUSTOMIZATION FIELDS */}
                {/* =========================================== */}

                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:space-y-5 sm:px-5 sm:py-5">
                  {(() => {
                    const price = Array.isArray(
                      selectedCustomizationItem?.prices,
                    )
                      ? selectedCustomizationItem.prices[0]
                      : null;

                    const customizations = Array.isArray(price?.customizations)
                      ? price.customizations
                      : [];

                    return customizations.map((customization, index) => {
                      const fieldName =
                        customization?.customize_field || `Option ${index + 1}`;

                      const type = customization?.customization_type;

                      const options = Array.isArray(customization?.options)
                        ? customization.options
                        : [];

                      return (
                        <div key={`${fieldName}-${index}`}>
                          <label className="mb-2 block text-[12px] font-semibold text-gray-800 sm:text-[13px]">
                            {fieldName}
                          </label>

                          {/* INPUT */}

                          {type === "input" && (
                            <input
                              type="text"
                              value={customizationValues[fieldName] || ""}
                              onChange={(event) =>
                                setCustomizationValues((previous) => ({
                                  ...previous,

                                  [fieldName]: event.target.value,
                                }))
                              }
                              placeholder={`Enter ${fieldName}`}
                              className="h-10 w-full rounded-xl border border-gray-200 px-3 text-[12px] outline-none transition focus:border-blue-500 sm:h-11 sm:text-sm"
                            />
                          )}

                          {/* OPTIONS */}

                          {(type === "select" || type === "customize") &&
                            options.length > 0 && (
                              <div className="grid grid-cols-3 gap-2 min-[480px]:flex min-[480px]:flex-wrap">
                                {options.map((option) => {
                                  const isSelected =
                                    customizationValues[fieldName] === option;

                                  return (
                                    <button
                                      key={option}
                                      type="button"
                                      onClick={() =>
                                        setCustomizationValues((previous) => ({
                                          ...previous,

                                          [fieldName]: option,
                                        }))
                                      }
                                      className={`min-w-0 break-words rounded-xl border px-2 py-2 text-center text-[10px] font-medium transition min-[480px]:rounded-full min-[480px]:px-3 min-[480px]:text-[12px] ${
                                        isSelected
                                          ? "border-blue-600 bg-blue-600 text-white"
                                          : "border-gray-200 bg-white text-gray-700 hover:border-blue-400"
                                      }`}
                                    >
                                      {option}
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                          {options.length === 0 && type !== "input" && (
                            <p className="text-[11px] text-gray-400 sm:text-[12px]">
                              No options available
                            </p>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* =========================================== */}
                {/* DONE */}
                {/* =========================================== */}

                <div className="shrink-0 border-t border-gray-200 px-4 py-3.5 pb-[max(14px,env(safe-area-inset-bottom))] sm:px-5 sm:py-4 sm:pb-4">
                  <button
                    type="button"
                    disabled={isRestaurantClosed}
                    onClick={() => {
                      if (isRestaurantClosed) {
                        return;
                      }

                      addItem(
                        selectedCustomizationItem,
                        selectedCustomizationItem?.category || {},
                        customizationValues,
                      );

                      setSelectedCustomizationItem(null);
                      setCustomizationValues({});
                    }}
                    className="
    h-10
    w-full
    rounded-xl
    bg-blue-700
    text-[12px]
    font-semibold
    text-white
    transition

    hover:bg-blue-800
    active:scale-[0.99]

    disabled:cursor-not-allowed
    disabled:bg-gray-300
    disabled:text-gray-500
    disabled:hover:bg-gray-300
    disabled:active:scale-100

    sm:h-11
    sm:text-sm
  "
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}

        <RepeatCustomizationModal
          open={Boolean(repeatCustomizationItem)}
          itemName={
            repeatCustomizationItem?.item?.item_name ||
            repeatCustomizationItem?.item?.itemName ||
            ""
          }
          preferences={repeatCustomizationItem?.preferences || []}
          onClose={() => setRepeatCustomizationItem(null)}
          onChooseNew={() => {
            const current = repeatCustomizationItem;

            setRepeatCustomizationItem(null);

            if (!current) {
              return;
            }

            handleCustomize(current.item, current.category);
          }}
          onRepeat={handleRepeatCustomization}
        />

        {/* =================================================== */}
        {/* CART DRAWER */}
        {/* =================================================== */}

        <CartDrawer
          open={cartOpen}
          onClose={() => setCartOpen(false)}
          onCheckout={handleCheckout}
        />
      </section>
    </>
  );
}

